// functional methods that can see dom, but can't access components or store
import cache from '@/cache.js'
import consts from '@/consts.js'
import codeLanguages from '@/data/codeLanguages.json'
import helloSpace from '@/data/hello.json'

import { nanoid } from 'nanoid'
import uniqBy from 'lodash-es/uniqBy'
import last from 'lodash-es/last'
import sortBy from 'lodash-es/sortBy'
import times from 'lodash-es/times'
import join from 'lodash-es/join'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import isToday from 'dayjs/plugin/isToday'
import { colord, extend } from 'colord'
import namesPlugin from 'colord/plugins/names'
import qs from '@aguezz/qs-parse'
import getCurvePoints from '@/libs/curve_calc.js'
import random from 'lodash-es/random'
import cloneDeep from 'lodash-es/cloneDeep'
// import merge from 'lodash-es/merge'
import randomColor from 'randomcolor'
// https://data.iana.org/TLD/tlds-alpha-by-domain.txt
// Updated Jun 9 2021 UTC
import tldsList from '@/data/tlds.json'
dayjs.extend(relativeTime)
dayjs.extend(isToday)
extend([namesPlugin])
let tlds = tldsList.join(String.raw`)|(\.`)
tlds = String.raw`(\.` + tlds + ')'

let isComposionEvent = false
let compositionEventEndTime = 0
if (typeof window !== 'undefined') {
  window.addEventListener('compositionstart', () => {
    isComposionEvent = true
  })
  window.addEventListener('compositionend', (event) => {
    isComposionEvent = false
    compositionEventEndTime = event.timeStamp
  })
}

const uuidLength = 21
const randomRGBA = (alpha) => {
  const hex = randomColor({ hue: 'random', luminosity: 'random' })
  const rgba = colord(hex).alpha(alpha).toRgbString()
  return rgba
}

export default {
  loadImage (src) {
    // from https://stackoverflow.com/a/5058336
    return new Promise((resolve, reject) => {
      const image = new Image()
      image.addEventListener('load', resolve)
      image.addEventListener('error', reject)
      image.src = src
    })
  },
  isKinopioUploadUrl (url) {
    // https://regexr.com/8f4h5
    // matches https://*.kinopio.club/
    const urlPattern = /^https:\/\/.+\.kinopio\.club\/?/i
    return urlPattern.test(url)
  },
  imgproxyUrl (url, maxDimensions) {
    if (!this.isKinopioUploadUrl(url)) {
      return url
    }
    const isGif = url.includes('.gif')
    if (isGif) { return url }
    if (maxDimensions) {
      return `${consts.imgproxyHost}/_/rs:fit:${maxDimensions}:${maxDimensions}:0/f:webp/plain/${encodeURIComponent(url)}`
    } else {
      return `${consts.imgproxyHost}/_/f:webp/plain/${url}`
    }
  },
  // the first frame of a gif, resized, because animated gifs are decoded at full size
  imgproxyStillGifUrl (url, maxDimensions) {
    if (!this.isKinopioUploadUrl(url)) {
      return url
    }
    return `${consts.imgproxyHost}/_/rs:fit:${maxDimensions}:${maxDimensions}:0/f:png/plain/${encodeURIComponent(url)}`
  },
  imgproxyBreakpointUrl (url, maxDimensions, shouldPauseGif) {
    // snapped to breakpoints so resizing and zooming don't request a new image every px
    const containerBreakpoints = [100, 200, 400, 600, 800, 1200, 3000]
    const devicePixelRatio = Math.round(window.devicePixelRatio || 1)
    const breakpoint = containerBreakpoints.find(breakpoint => maxDimensions <= breakpoint)
    const isGif = url.includes('.gif')
    if (shouldPauseGif && isGif) {
      const stillBreakpoint = breakpoint || last(containerBreakpoints)
      return this.imgproxyStillGifUrl(url, stillBreakpoint * devicePixelRatio)
    }
    if (!breakpoint) { return this.imgproxyUrl(url) }
    return this.imgproxyUrl(url, breakpoint * devicePixelRatio)
  },
  mobileTouchPosition (event, type) {
    let touch
    if (event.touches[0]) {
      touch = event.touches[0]
    } else {
      // touchend
      touch = event.changedTouches[0]
    }
    return {
      x: touch[`${type}X`],
      y: touch[`${type}Y`]
    }
  },
  elementShouldBeOnRightSide (element) {
    if (!element) { return }
    element = element.getBoundingClientRect()
    const viewport = this.visualViewport()
    const offset = viewport.width - (element.x + element.width)
    if (offset < 0) {
      return true
    } else {
      return false
    }
  },
  elementHeightFromHeader (element, isChildElement) {
    if (!element) { return }
    const viewport = this.visualViewport()
    const rect = element.getBoundingClientRect()
    let header = document.querySelector('header')
    if (!header) { return }
    header = header.getBoundingClientRect()
    let height = viewport.height - header.bottom - (viewport.height - rect.bottom)
    if (isChildElement) {
      const dialog = element.closest('dialog')
      const dialogRect = dialog.getBoundingClientRect()
      height = height - (rect.y - dialogRect.y)
    }
    return height
  },
  elementHeight (element, isChildElement) {
    if (!element) { return }
    const threshold = 10
    const rect = element.getBoundingClientRect()
    let height
    const viewportHeight = this.visualViewport().height
    height = viewportHeight - rect.y - threshold
    if (isChildElement) {
      const dialog = element.closest('dialog')
      const dialogRect = dialog.getBoundingClientRect()
      height = height - (rect.y - dialogRect.y)
    }
    return height
  },
  dialogIsVisible () {
    const elements = document.querySelectorAll('dialog')
    const dialogs = []
    elements.forEach(dialog => {
      if (dialog.className !== 'card-details') {
        dialogs.push(dialog)
      }
    })
    const dialogIsVisible = Boolean(dialogs.length)
    return dialogIsVisible
  },
  unpinnedDialogIsVisible () {
    const dialogs = document.querySelectorAll('dialog')
    // ignore pinned dialogs
    const pinnedDialogs = []
    dialogs.forEach(dialog => {
      if (dialog.dataset.isPinned === 'true') {
        pinnedDialogs.push(dialog)
      }
    })
    if (dialogs.length === pinnedDialogs.length) {
      return false
    } else if (dialogs.length) {
      return true
    } else {
      return false
    }
  },
  // spaces replace native pinch zoom with space zoom
  disablePinchZoom () {
    const viewport = document.querySelector('head meta[name=viewport]')
    viewport.setAttribute('content', 'width=device-width, initial-scale=1, minimum-scale=1, maximum-scale=1, user-scalable=no')
  },
  enablePinchZoom () {
    const viewport = document.querySelector('head meta[name=viewport]')
    viewport.setAttribute('content', 'width=device-width, initial-scale=1') // index.html default
  },
  cursorPositionInViewport (event) {
    let x, y
    if (event.touches) {
      const touch = this.mobileTouchPosition(event, 'client')
      x = touch.x
      y = touch.y
    } else {
      x = event.clientX
      y = event.clientY
    }
    return { x, y }
  },
  cursorPositionInPage (event) {
    if (!event) { return }
    let x, y
    if (event.touches) {
      const touch = this.mobileTouchPosition(event, 'page')
      x = touch.x
      y = touch.y
    } else if (event.type === 'keyup' || event.type === 'keydown') {
      const rect = event.target.getBoundingClientRect()
      x = rect.x + window.pageXOffset
      y = rect.y + window.pageYOffset
    } else {
      x = event.pageX
      y = event.pageY
    }
    return { x, y }
  },
  cursorPositionInSpace (event, position) {
    position = position || this.cursorPositionInPage(event)
    if (!position) { return }
    const space = document.getElementById('space')
    if (!space) { return }
    const spaceRect = space.getBoundingClientRect()
    const client = {
      x: position.x - window.scrollX,
      y: position.y - window.scrollY
    }
    const zoom = this.spaceZoomDecimal() || 1
    return {
      x: Math.round((client.x - spaceRect.x) / zoom),
      y: Math.round((client.y - spaceRect.y) / zoom)
    }
  },
  rectDimensions (rect) {
    const zoom = this.spaceCounterZoomDecimal() || 1
    rect.x = rect.x + window.scrollX
    rect.y = rect.y + window.scrollY
    const rectPosition = this.updatePositionWithSpaceOffset(rect)
    return {
      x: Math.round(rectPosition.x * zoom),
      y: Math.round(rectPosition.y * zoom),
      width: Math.round((rect.width || rect.resizeWidth) * zoom),
      height: Math.round((rect.height || rect.resizeHeight) * zoom)
    }
  },
  isPositionOutsideOfSpace (position) {
    const isOutsideX = position.x < 0
    const isOutsideY = position.y < 0
    return isOutsideX || isOutsideY
  },
  outsideSpaceOffset () {
    const space = document.getElementById('space')
    if (!space) { return }
    const spaceRect = space.getBoundingClientRect()
    return {
      x: Math.round(spaceRect.x + window.scrollX),
      y: Math.round(spaceRect.y + window.scrollY)
    }
  },
  updatePositionWithSpaceOffset (position) {
    const spaceOffset = this.outsideSpaceOffset()
    if (!spaceOffset) { return position }
    return {
      x: position.x - spaceOffset.x,
      y: position.y - spaceOffset.y
    }
  },
  childDialogPositionFromParent ({ element, offsetX, offsetY, shouldIgnoreZoom, maxYOffset, transformOriginIsTopRight }) {
    element = element.closest('li') || element.closest('.badge') || element.closest('button') || element
    offsetX = offsetX || 0
    offsetY = offsetY || 0
    const rect = element.getBoundingClientRect()
    const position = this.coordsWithCurrentScrollOffset({ x: rect.x, y: rect.y, shouldIgnoreZoom })
    let zoom = this.spaceCounterZoomDecimal() || 1
    if (shouldIgnoreZoom) {
      zoom = 1
    }
    const indent = 8 * zoom
    const x = position.x + offsetX + indent
    let y = position.y + rect.height + offsetY - indent
    if (maxYOffset) {
      const maxY = this.visualViewport().height - maxYOffset + window.scrollY
      y = Math.min(maxY, y)
    }
    return { x, y, shouldIgnoreZoom, transformOriginIsTopRight, zoom }
  },
  visualViewport () {
    const visualViewport = window.visualViewport
    let viewport
    if (visualViewport) {
      viewport = {
        width: visualViewport.width,
        height: visualViewport.height,
        scale: visualViewport.scale,
        offsetLeft: Math.max(visualViewport.offsetLeft, 0),
        offsetTop: Math.max(visualViewport.offsetTop, 0),
        pageLeft: visualViewport.pageLeft,
        pageTop: visualViewport.pageTop
      }
    } else {
      // firefox fallback, doesn't support pinch zooming
      viewport = {
        width: document.documentElement.clientWidth,
        height: document.documentElement.clientHeight,
        scale: document.documentElement.clientWidth / window.innerWidth,
        offsetLeft: 0,
        offsetTop: 0,
        pageLeft: window.scrollX,
        pageTop: window.scrollY
      }
    }
    viewport.offsetLeft = Math.round(viewport.offsetLeft)
    viewport.offsetTop = Math.round(viewport.offsetTop)
    viewport.pageLeft = Math.round(viewport.pageLeft)
    viewport.pageTop = Math.round(viewport.pageTop)
    return viewport
  },
  rectCenter (rect) {
    const x = Math.round(rect.x + (rect.width / 2))
    const y = Math.round(rect.y + (rect.height / 2))
    return { x, y }
  },
  isBetween ({ value, min, max }) {
    if (min <= value && value <= max) { return true }
  },
  percentageBetween ({ value, min, max }) {
    return ((value - min) / (max - min)) * 100
  },
  isNullish (value) {
    return value === null || value === undefined || Number.isNaN(value)
  },
  clone (object) {
    if (!object) { return }
    this.typeCheck({ value: object, type: 'object' })
    return cloneDeep(object)
  },
  isUndefinedOrNull (value) {
    return value === undefined || value === null
  },
  typeCheck ({ value, type, allowUndefined, origin, silenceWarning }) {
    if (allowUndefined && this.isUndefinedOrNull(value)) {
      return true
    }
    if (type === 'array' && Array.isArray(value)) {
      return true
    }
    if (typeof value !== type) { // eslint-disable-line valid-typeof
      if (!silenceWarning) {
        console.error(`🚑 passed value is not ${type}`, value, origin)
      }
      return false
    } else {
      return true
    }
  },
  oppositeSide (side) {
    if (side === 'left') {
      return 'right'
    }
    if (side === 'right') {
      return 'left'
    }
    if (side === 'top') {
      return 'bottom'
    }
    if (side === 'bottom') {
      return 'top'
    }
  },
  numberOfLeadingTabs (string) {
    // https://regexr.com/6dl8u
    // matches /t tab characters at start of string
    const tabPattern = /^(\t)+/g
    const match = string.match(tabPattern)
    if (!match) { return 0 }
    const tabs = match[0].length
    return tabs
  },
  numberOfLeadingDoubleSpaces (string) {
    // https://regexr.com/6dl9m
    // matches two space characters at start of string
    const doubleSpacesPattern = /^( {2})+/g
    const match = string.match(doubleSpacesPattern)
    if (!match) { return 0 }
    const doubleSpaces = match[0].length / 2
    return doubleSpaces
  },
  roundFloat (number) {
    // https://stackoverflow.com/a/9453447
    // returns 1.23
    return Math.round(number * 100) / 100
  },
  roundToNearest (value, divider) {
    divider = divider || consts.spaceBetweenCards
    value = value || 1
    const increment = Math.round(value / divider)
    return increment * divider
  },
  pointIsEmpty (point) {
    if (!point) { return }
    if (point.x === 0 && point.y === 0) { return true }
  },
  arrayHasItems (array) { // !arrayIsEmpty, arrayExists
    this.typeCheck({ value: array, type: 'array', allowUndefined: true, origin: 'arrayHasItems' })
    if (array) {
      array = array.filter(item => Boolean(item))
      if (array.length) {
        return true
      }
    }
    return false
  },
  countArrayItems (obj) {
    let total = 0
    for (const value of Object.values(obj)) {
      if (Array.isArray(value)) {
        total += value.length
      }
    }
    return total
  },
  longestStringInArray (array) {
    this.typeCheck({ value: array, type: 'array', origin: 'longestStringInArray' })
    let longest = ''
    array.forEach(string => {
      if (string.length > longest.length) {
        longest = string
      }
    })
    return longest
  },
  arrayToString (array) {
    // ['yo', 'lo'] to 'yo lo'
    // const string = array
    let string = ''
    array.forEach(item => {
      string = string + item + ' '
    })
    return string.trim()
  },
  isStringJSON (string) {
    let isString = true
    try {
      JSON.parse(string)
    } catch (error) {
      isString = false
    }
    return isString
  },
  normalizeToObject (item) {
    if (typeof item === 'string') {
      item = JSON.parse(item)
    }
    return item
  },
  updateObject (object, updates) {
    this.typeCheck({ value: updates, type: 'object', origin: 'updateObject' })
    const keys = Object.keys(updates)
    if (keys.length === 0) {
      object = {}
    } else {
      keys.forEach(key => {
        object[key] = updates[key]
      })
    }
    return object
  },
  objectPickKeys (object, keys) {
    try {
      const result = {}
      keys.forEach(key => {
        result[key] = object[key]
      })
      return result
    } catch (error) {
      console.error('🚒 objectPickKeys', error, object, keys)
    }
  },
  updateUsersWithUser (users, updatedUser, keys) {
    keys = keys || ['name', 'color', 'description', 'website']
    this.typeCheck({ value: users, type: 'object', origin: 'updateUsersWithUser' })
    this.typeCheck({ value: updatedUser, type: 'object', origin: 'updateUsersWithUser' })
    this.typeCheck({ value: keys, type: 'array', origin: 'updateUsersWithUser' })
    return users.map(user => {
      if (user.id === updatedUser.userId) {
        keys.forEach(key => {
          user[key] = updatedUser[key] || user[key]
        })
      }
      return user
    })
  },
  userIsUpgraded (user) {
    return Boolean(user.stripeSubscriptionId || user.stripePlanIsPurchased || user.downgradeAt || user.appleSubscriptionIsActive)
  },
  splitArrayIntoChunks (array, chunkSize) {
    const numberOfChunks = Math.ceil(array.length / chunkSize)
    const chunks = []
    times(numberOfChunks, function (index) {
      const start = index * chunkSize
      const end = (index + 1) * chunkSize
      const chunk = array.slice(start, end)
      chunks.push(chunk)
    })
    return chunks
  },
  findInArrayOfObjects (array, key, value) {
    return array.find(item => item[key] === value)
  },
  cursorsAreClose (startCursor, endCursor, zoom) {
    if (!startCursor) { return }
    if (!endCursor) { return }
    zoom = zoom || this.spaceCounterZoomDecimal()
    let threshold = 5
    threshold = threshold * zoom
    const xRange = {
      value: endCursor.x,
      min: startCursor.x - threshold,
      max: startCursor.x + threshold
    }
    const yRange = {
      value: endCursor.y,
      min: startCursor.y - threshold,
      max: startCursor.y + threshold
    }
    return this.isBetween(xRange) && this.isBetween(yRange)
  },
  objectHasKeys (object) {
    if (!object) { return }
    if (Object.keys(object).length) {
      return true
    } else {
      return false
    }
  },
  isIPhone () {
    // iPads identify as MacIntosh
    return navigator.platform && /iPhone|iPod/.test(navigator.platform)
  },
  isAndroid () {
    return navigator.platform && (/Android/.test(navigator.platform) || /Android/.test(navigator.userAgent))
  },
  isSafari () {
    return /Safari/.test(navigator.userAgent)
  },
  isMobile () {
    return Boolean(this.isIPhone() || this.isAndroid())
  },
  isFirefox () {
    return navigator.userAgent && /Firefox/.test(navigator.userAgent)
  },
  isLinux () {
    return navigator.platform.includes('Linux')
  },
  isMultiTouch (event) {
    if (event.touches) {
      return event.touches.length > 1
    }
  },
  // non-latin (IME) input for japanese kanji
  isCompositionKeyboardEvent (event) {
    if (isComposionEvent) { return true }
    if (event.isComposing) { return true }
    // older chromium and android fallback
    if (event.keyCode === 229) { return true }
    // safari fallback
    if (compositionEventEndTime && event.timeStamp) {
      return Math.abs(event.timeStamp - compositionEventEndTime) < 100
    }
    return false
  },
  focusTextarea (element) {
    if (!element) { return }
    element.focus()
    if (this.isMobile()) { return } // ios only shows the onscreen keyboard when focused within the tap event
    setTimeout(() => { // use setTimeout focus to prevent 1password lag
      element.focus()
    }, 1)
  },
  isEventTouchOrMouseLeftButton (event) {
    const isMouseEvent = event.type.includes('mouse')
    if (!isMouseEvent) {
      // ignore touch events
      return true
    }
    return event.button === 0
  },
  isMacOrIpad () {
    return window.navigator.platform === 'MacIntel'
  },
  metaKey () {
    if (this.isMacOrIpad() || this.isIPhone()) {
      return '⌘'
    } else {
      return 'Ctrl'
    }
  },
  optionKey () {
    if (this.isMacOrIpad() || this.isIPhone()) {
      return 'Opt'
    } else {
      return 'Alt'
    }
  },
  cardUserAtMentionString (user) {
    let userString = '@' + this.normalizeString(user.name)
    if (!user.name) {
      userString = '@anon' + user.id.slice(user.id.length - 4, user.id.length)
    }
    return userString
  },
  cardDateAtMentionString (date) {
    let dateString = this.shortAbsoluteDate(date).toLowerCase()
    dateString = this.normalizeString(dateString)
    return '@' + dateString // @jan-2-2026
  },
  splitCardNameByParagraphAndSentence (prevName) {
    const maxCardCharacterLimit = consts.cardCharacterLimit
    const paragraphs = this.splitByParagraphs(prevName) || []
    let cardNames = paragraphs.map(paragraph => {
      let sentences
      if (paragraph.length > maxCardCharacterLimit) {
        sentences = this.splitBySentences(paragraph)
      }
      return sentences || paragraph
    })
    cardNames = cardNames.flat()
    // split names longer than max card length
    cardNames = cardNames.map(name => {
      // recursive
      const results = []
      let shouldSplit, nameToSplit
      do {
        shouldSplit = false
        nameToSplit = nameToSplit || name
        results.push(nameToSplit.substring(0, maxCardCharacterLimit))
        const otherSplit = nameToSplit.substring(maxCardCharacterLimit)
        if (otherSplit.length <= maxCardCharacterLimit) {
          results.push(otherSplit)
        } else {
          nameToSplit = otherSplit
          shouldSplit = true
        }
      } while (shouldSplit)

      if (results.length) { return results }
      return name
    })
    cardNames = cardNames.flat()
    cardNames = cardNames.filter(name => Boolean(name.length))
    return cardNames
  },
  splitBySentences (string) {
    if (!string) { return }
    let sentences = string.split('. ')
    sentences = sentences.filter(sentence => Boolean(sentence.length))
    // re-add sentence periods removed by split
    sentences = sentences.map((sentence, index) => {
      if (index < sentences.length - 1) {
        sentence = sentence + '.'
      }
      return sentence
    })
    return sentences
  },
  splitByParagraphs (string) {
    if (!string) { return }
    let paragraphs = string.split('\n')
    paragraphs = paragraphs.filter(paragraph => Boolean(paragraph.length))
    return paragraphs
  },
  capitalizeFirstLetter (string) {
    // 'dreams' -> 'Dreams'
    return string.charAt(0).toUpperCase() + string.slice(1)
  },
  removeTrailingPeriod (string) {
    // https://regexr.com/5784j
    return string.replace(/\.$/g, '')
  },
  clearTrailingSlash (string) {
    if (!string) { return }
    // https://regexr.com/68l08
    return string.replace(/\/$/g, '')
  },
  pastTense (string) {
    if (string === 'cut') { return string }
    const lastLetter = string.charAt(string.length - 1)
    // add test cases ad hoc from https://github.com/boo1ean/tensify
    if (lastLetter === 'e') {
      return string + 'd' // move → moved
    } else if (lastLetter === 'y') {
      return string.substring(0, string.length - 1) + 'ied' // copy → copied
    } else {
      return string + 'ed'
    }
  },
  lowercaseFirstLetter (string) {
    // 'Dreams' -> 'dreams'
    return string.charAt(0).toLowerCase() + string.slice(1)
  },
  convertHTMLEntities (string) {
    const entities = [
      { code: '&#60;', result: '<' },
      { code: '&#62;', result: '>' },
      { code: '&#38;', result: '&' },
      { code: '&#34;', result: '"' },
      { code: '&#39;', result: "'" }
    ]
    entities.forEach(entity => {
      string = string.replace(entity.code, entity.result)
    })
    return string
  },
  updateAllIds (object, key, idDeltas) {
    const index = idDeltas.findIndex(id => object[key] === id.prevId)
    if (index >= 0) {
      return idDeltas[index].newId
    } else {
      return object[key]
    }
  },
  pluralize (word, condition) {
    if (condition || condition === 0) {
      word = word + 's'
    }
    return word
  },
  truncated (string, limit, character) {
    character = character || '…'
    if (!string) { return '' }
    limit = limit || 60
    if (string.length <= limit) { return string }
    string = string.substring(0, limit) + character
    return string
  },
  insertStringAtIndex (string, insert, index) {
    return string.substr(0, index) + insert + string.substr(index)
  },
  insertIntoArray (array, value, index) {
    const start = array.slice(0, index)
    const end = array.slice(index, array.length)
    start.push(value)
    const newArray = start.concat(end)
    return newArray
  },
  removeFromArray (array, index) {
    delete array[index]
    array = array.filter(item => Boolean(item))
    return array
  },
  generateRange (start, end) {
    // converts 0,2 to [0,1,2]
    const rangeArray = []
    for (let i = start; i <= end; i++) {
      rangeArray.push(i)
    }
    return rangeArray
  },
  unixTime (date) {
    date = date || new Date()
    return new Date(date).toISOString()
  },
  shortRelativeTime (date) {
    if (!date) { return }
    date = date.toString()
    let time = dayjs(date).fromNow(true)
    // https://day.js.org/docs/en/customization/relative-time
    time = time.replace('a few seconds', 'now')
    time = time.replace('a minute', '1m')
    time = time.replace(' minutes', 'm')
    time = time.replace('an hour', '1h')
    time = time.replace(' hours', 'h')
    time = time.replace('a day', '1d')
    time = time.replace(' days', 'd')
    time = time.replace('a month', '1mo')
    time = time.replace(' months', 'mo')
    time = time.replace('a year', '1yr')
    time = time.replace(' years', 'y')
    return time
  },
  dateIsToday (date) {
    return dayjs(date).isToday()
  },
  dateIsPast (date) {
    return dayjs(date).isBefore(dayjs(), 'day')
  },
  shortRelativeDate (date) {
    if (!date) { return }
    date = dayjs(date).startOf('day')
    const today = dayjs().startOf('day')
    const diff = date.diff(today, 'day')
    const yearsPast = today.diff(date, 'year')
    const monthsPast = today.diff(date, 'month')
    if (diff === 0) { return 'Today' }
    if (diff === 1) { return 'Tomorrow' }
    if (diff === -1) { return 'Yesterday' }
    if (diff > 1) { return `${diff} days left` }
    // past dates
    if (yearsPast === 1) { return 'a year ago' }
    if (yearsPast > 1) { return `${yearsPast} years ago` }
    if (monthsPast === 1) { return 'a month ago' }
    if (monthsPast > 1) { return `${monthsPast} months ago` }
    return `${-diff} days ago`
  },
  shortAbsoluteDate (date) {
    if (!date) { return }
    return dayjs(date).format('MMM D, YYYY') // Jan 20, 2026
  },
  isEvenNumber (number) {
    if (number % 2 === 0) {
      return true
    }
  },
  averageOfNumbers (numbers) {
    this.typeCheck({ value: numbers, type: 'array', origin: 'averageOfNumbers' })
    let total = 0
    numbers.forEach(number => {
      total += number
    })
    return total / numbers.length
  },
  nearestItems (currentItem, items) {
    const max = 6
    items = items.map(item => {
      item.distance = this.distanceBetweenTwoPoints(item, currentItem)
      return item
    })
    items = sortBy(items, ['distance'])
    items = items.slice(0, max)
    return items
  },
  distanceBetweenTwoPoints (point1, point2) {
    if (!point1 || !point2) { return }
    // https://www.mathwarehouse.com/algebra/distance_formula/index.php
    const xDelta = Math.abs(point1.x - point2.x)
    const yDelta = Math.abs(point1.y - point2.y)
    const distance = Math.sqrt((xDelta ** 2) + (yDelta ** 2))
    return Math.round(distance)
  },
  angleBetweenTwoPoints (point1, point2) {
    // https://www.mathisfunforum.com/viewtopic.php?id=16175
    const xDelta = Math.abs(point1.x - point2.x)
    const yDelta = Math.abs(point1.y - point2.y)
    const angleRadians = Math.atan2(yDelta, xDelta)
    const angleDegrees = angleRadians * 180 / Math.PI
    return Math.round(angleDegrees)
  },
  pointBetweenTwoPoints (point1, point2) {
    // https://gamedev.stackexchange.com/questions/23430/get-points-on-a-line-between-two-points
    const progress = 0.5
    const point = {
      x: point1.x + (point2.x - point1.x) * progress,
      y: point1.y + (point2.y - point1.y) * progress
    }
    return point
  },
  pointsBetweenTwoPoints (point1, point2) {
    // [x1, y1, x2, y2, ...]
    const splinePoints = getCurvePoints([point1.x, point1.y, point2.x, point2.y])
    // [{x1, y2}, {x2, y2}, ...]
    const points = []
    let prevX
    splinePoints.forEach((value, index) => {
      value = Math.round(value)
      const isX = this.isEvenNumber(index)
      if (isX) {
        prevX = value
      } else {
        const point = { x: prevX, y: value }
        points.push(point)
      }
    })
    return points
  },
  innerHTMLText (htmlString) {
    // https://regexr.com/6olpg
    // from https://stackoverflow.com/a/1736801
    // matches open and close tags
    const htmlTagPattern = new RegExp(/<(?:"[^"]*"['"]*|'[^']*'['"]*|[^'">])+>/gim)
    return htmlString.replace(htmlTagPattern, '')
  },
  decodeEntitiesFromHTML (string) {
    const element = document.createElement('textarea')
    element.innerHTML = string
    const value = element.value
    element.remove()
    return value
  },

  // colors

  cssVariable (name) {
    return getComputedStyle(document.documentElement).getPropertyValue(`--${name}`)
  },
  colorIsValid (color) {
    return colord(color).isValid()
  },
  colorIsDark (color, brightnessThreshold) {
    brightnessThreshold = brightnessThreshold || 0.4
    if (!color) { return }
    if (color === 'transparent') { return }
    return colord(color).brightness() < brightnessThreshold
  },
  invertColor (color) {
    return colord(color).invert().toHex()
  },
  setCssVariable (key, value) {
    document.documentElement.style.setProperty(`--${key}`, value)
  },
  colorsAreEqual (color1, color2) {
    color1 = this.colorToRGB(color1)
    color2 = this.colorToRGB(color2)
    return color1 === color2
  },
  colorToRGB (color) {
    const { r, g, b } = colord(color).toRgb()
    return `rgba(${r}, ${g}, ${b})`
  },
  alternateColor (color, isThemeDark, colorDelta) {
    colorDelta = colorDelta || 0.1
    if (isThemeDark.value) {
      color = colord(color).lighten(colorDelta).toHex()
    } else {
      color = colord(color).darken(colorDelta).toHex()
    }
    return color
  },
  safeColor (color) {
    const brightnessThreshold = 0.5
    const brightness = colord(color).brightness()
    const isTooBright = brightness > 0.8
    const isTooDark = brightness < 0.2
    if (isTooBright) {
      color = colord(color).darken(brightnessThreshold).toHex()
    } else if (isTooDark) {
      color = colord(color).lighten(brightnessThreshold).toHex()
    }
    return color
  },
  colorClasses ({ backgroundColor, backgroundColorIsDark }) {
    backgroundColorIsDark = backgroundColorIsDark || this.colorIsDark(backgroundColor)
    const classes = []
    if (backgroundColorIsDark) {
      classes.push('is-background-dark')
    } else {
      classes.push('is-background-light')
    }
    return classes
  },

  // normalize items

  normalizeItems (items) {
    if (!items?.length) return items
    return Object.fromEntries(
      items
        .filter(Boolean)
        .map(item => [item.id, item])
    )
  },
  denormalizeItems (normalizedItems) {
    return Object.values(normalizedItems)
  },
  stringToBoolean (string) {
    if (string === 'true') {
      return true
    }
  },

  // items (cards, boxes, or lists)

  itemElement (itemId) {
    const card = this.cardElementFromId(itemId)
    const box = this.boxElementFromId(itemId)
    const list = this.listElementFromId(itemId)
    return card || box || list
  },
  itemElementDimensions (item) {
    if (!item) { return }
    const card = this.cardElementFromId(item.id)
    const box = this.boxElementFromId(item.id)
    const list = this.listElementFromId(item.id)
    let rect
    if (card) {
      rect = this.cardElementDimensions(item)
    } else if (box) {
      rect = this.boxElementDimensions(item)
    } else if (list) {
      rect = this.listElementDimensions(item)
    }
    return rect
  },
  sortByAlphabetical (items, property) {
    const sorted = items.sort((a, b) => {
      // Case-insensitive comparison, ignore emojis
      let propA = this.normalizeString(a[property]).trim()
      let propB = this.normalizeString(b[property]).trim()
      // remove leading dashes
      propA = this.removeLeadingDashes(propA)
      propB = this.removeLeadingDashes(propB)
      if (propA === propB) {
        // If stripped values are equal, fall back to comparing the original strings
        return a[property] < b[property] ? -1 : 1
      }
      return propA < propB ? -1 : 1
    })
    return sorted
  },
  sortByY (items) {
    return items.sort((a, b) => {
      return a.y - b.y
    })
  },
  sortByX (items) {
    return items.sort((a, b) => {
      return a.x - b.x
    })
  },
  sortByDistanceFromOrigin (items) {
    items = this.clone(items)
    items = items.map(item => {
      item.distance = Math.sqrt(Math.pow(item.x, 2) + Math.pow(item.y, 2))
      return item
    })
    items = sortBy(items, ['distance'])
    return items
  },
  sortByCreatedAt (items) {
    const sortedItems = items.sort((a, b) => {
      const bCreatedAt = dayjs(b.createdAt).unix()
      const aCreatedAt = dayjs(a.createdAt).unix()
      return bCreatedAt - aCreatedAt
    })
    return sortedItems
  },
  sortByUpdatedAt (items) {
    items = items.map(space => {
      space.updatedAt = space.editedAt || space.updatedAt || space.createdAt
      if (!space.updatedAt) {
        space.updatedAt = dayjs().subtract(100, 'year')
      }
      return space
    })
    items = items.filter(item => Boolean(item))
    const sortedItems = items.sort((a, b) => {
      const bUpdatedAt = dayjs(b.updatedAt).unix()
      const aUpdatedAt = dayjs(a.updatedAt).unix()
      return bUpdatedAt - aUpdatedAt
    })
    return sortedItems
  },

  nameTextEditAction ({ action, startPosition, endPosition, name }) {
    name = name || ''
    let newName, offset
    let md = ''
    if (action === 'bold') {
      md = '**'
    } else if (action === 'italic') {
      md = '_'
    }
    const length = md.length
    const before = name.slice(0, startPosition)
    const selected = name.slice(startPosition, endPosition)
    const after = name.slice(endPosition)
    const start = before.endsWith(md)
    const end = after.startsWith(md)

    if (start && end) {
      // remove md
      newName = before.slice(0, -length) + selected + after.slice(length)
      offset = -length
    } else {
      // add md
      newName = before + `${md}${selected}${md}` + after
      offset = length
    }
    return { newName, offset }
  },
  nameTextEditLinkAction ({ startPosition, endPosition, name }) {
    name = name || ''
    // lol → [lol](url)
    const before = name.slice(0, startPosition)
    const selected = name.slice(startPosition, endPosition)
    const after = name.slice(endPosition)
    const newName = before + '[' + selected + '](url)' + after
    // select 'url'
    const newStartPosition = endPosition + 3 // [](
    const newEndPosition = newStartPosition + 3 // url
    return { newName, newStartPosition, newEndPosition }
  },

  // Cards

  cardElement (card) {
    return document.querySelector(`.card-wrap[data-card-id="${card.id}"]`)
  },
  cardElementDimensions (card) {
    if (!card) { return }
    card = this.clone(card)
    const element = this.cardElement(card)
    if (!element) { return }
    const cardId = card.id
    card.shouldRender = this.stringToBoolean(element.dataset.shouldRender)
    card.isLocked = this.stringToBoolean(element.dataset.isLocked)
    card.x = parseInt(element.dataset.x)
    card.y = parseInt(element.dataset.y)
    const width = parseInt(element.dataset.resizeWidth || element.dataset.width)
    const height = parseInt(element.dataset.height)
    card.width = Math.ceil(width)
    card.height = Math.ceil(height)
    card.id = cardId
    card.itemType = 'card'
    return card
  },
  boxElementDimensions (box) {
    if (!box) { return }
    const element = this.boxElementFromId(box.id)
    if (!element) { return }
    box.shouldRender = this.stringToBoolean(element.dataset.shouldRender)
    box.isLocked = this.stringToBoolean(element.dataset.isLocked)
    box.x = parseInt(element.dataset.x)
    box.y = parseInt(element.dataset.y)
    box.resizeWidth = parseInt(element.dataset.resizeWidth)
    box.resizeHeight = parseInt(element.dataset.resizeHeight)
    box.width = parseInt(element.dataset.resizeWidth)
    box.height = parseInt(element.dataset.resizeHeight)
    box.infoWidth = parseInt(element.dataset.infoWidth)
    box.infoHeight = parseInt(element.dataset.infoHeight)
    box.itemType = 'box'
    return box
  },
  listElementDimensions (list) {
    if (!list) { return }
    const element = this.listElementFromId(list.id)
    if (!element) { return }
    // list.shouldRender = element.dataset.shouldRender
    list.x = parseInt(element.dataset.x)
    list.y = parseInt(element.dataset.y)
    list.width = parseInt(element.dataset.width)
    list.height = parseInt(element.dataset.height)
    if (element.dataset.isCollapsed === 'true') {
      list.height = consts.listInfoHeight
    }
    list.itemType = 'list'
    return list
  },
  clearAllCardDimensions (card) {
    const cardWrapElement = document.querySelector(`.card-wrap[data-card-id="${card.id}"]`)
    const cardElement = document.querySelector(`.card[data-card-id="${card.id}"]`)
    const contentWrapElement = cardWrapElement.querySelector('.card-content-wrap')
    const cardMediaElement = cardWrapElement.querySelector('.media-card')
    cardWrapElement.style.width = null
    cardWrapElement.style.height = null
    cardElement.style.width = null
    contentWrapElement.style.width = null
    contentWrapElement.style.height = null
    if (cardMediaElement) {
      cardMediaElement.style.width = null
    }
    cardWrapElement.style.maxWidth = null
  },
  isMissingDimensions (item) {
    return !item.width || !item.height
  },
  topLeftItem (items) {
    items = this.clone(items)
    let shortestDistanceItem = {}
    items.forEach(item => {
      item.distance = Math.sqrt(Math.pow(item.x, 2) + Math.pow(item.y, 2))
      if (!shortestDistanceItem.distance) {
        shortestDistanceItem = item
      } else if (item.distance < shortestDistanceItem.distance) {
        shortestDistanceItem = item
      }
    })
    return shortestDistanceItem
  },
  cardElementFromPosition (x, y) {
    const elements = document.elementsFromPoint(x, y)
    let cardElement
    elements.find(element => {
      const match = element.closest('.card-wrap')
      if (match) {
        cardElement = match
      }
      return match
    })
    return cardElement
  },
  cardElementFromId (cardId) {
    return document.querySelector(`.card-wrap[data-card-id="${cardId}"]`)
  },
  cardRectFromId (cardId) {
    const element = this.cardElementFromId(cardId)
    if (!element) { return }
    return element.getBoundingClientRect()
  },
  cardPositionFromElement (cardId) {
    const element = this.cardElementFromId(cardId)
    if (!element) { return }
    const x = parseInt(element.style.left)
    const y = parseInt(element.style.top)
    return { x, y }
  },

  // boxes

  boxElementFromConnectorPosition (x, y) {
    const elements = document.elementsFromPoint(x, y)
    let boxFromConnector
    const boxElement = elements.find(element => {
      const classes = Array.from(element.classList)
      if (classes.includes('connector')) {
        boxFromConnector = element.closest('.box')
        return element.closest('.box')
      }
      return classes.includes('box-info')
    })
    return boxFromConnector || boxElement
  },
  boxElementFromId (boxId) {
    return document.querySelector(`.box[data-box-id="${boxId}"]`)
  },
  boxBackgroundElementFromId (boxId) {
    return document.querySelector(`.box-background[data-box-id="${boxId}"]`)
  },
  boxRectFromId (boxId) {
    const element = this.boxElementFromId(boxId)
    if (!element) { return }
    return element.getBoundingClientRect()
  },
  boxPositionFromElement (boxId) {
    const element = this.boxElementFromId(boxId)
    const x = parseInt(element.style.left)
    const y = parseInt(element.style.top)
    return { x, y }
  },
  boxInfoPositionFromId (boxId) {
    const element = document.querySelector(`.box-info[data-box-id="${boxId}"]`)
    if (!element) { return }
    const boxInfoRect = element.getBoundingClientRect()
    const infoWidth = Math.round(boxInfoRect.width + 4)
    const infoHeight = Math.round(boxInfoRect.height)
    return { infoWidth, infoHeight }
  },

  // lines

  lineElementFromId (lineId) {
    return document.querySelector(`.line-info[data-line-id="${lineId}"]`)
  },

  // lists

  listElementFromId (listId) {
    return document.querySelector(`.list[data-list-id="${listId}"]`)
  },
  listInfoElementFromId (listId) {
    return document.querySelector(`.list-info[data-list-id="${listId}"]`)
  },
  listElementFromConnectorPosition (x, y) {
    const elements = document.elementsFromPoint(x, y)
    const listInfoElement = elements.find(element => {
      const classes = Array.from(element.classList)
      return classes.includes('list-info')
    })
    if (!listInfoElement) { return }
    return listInfoElement.closest('.list')
  },
  listInfoRectFromId (listId) {
    const element = document.querySelector(`.list-info[data-list-id="${listId}"]`)
    const rect = element.getBoundingClientRect()
    return this.rectDimensions(rect)
  },
  listSiblingPositionIndex (listCards, targetPositionIndex, shouldPrepend) {
    let index = listCards.findIndex(card => card.listPositionIndex === targetPositionIndex)
    if (shouldPrepend) {
      index -= 1
    } else {
      index += 1
    }
    if (index < 0) { return null }
    const siblingCard = listCards[index]
    if (!siblingCard) {
      return null
    } else {
      return siblingCard.listPositionIndex
    }
  },
  listChildPlaceholderRectFromCardId (cardId) {
    const element = document.querySelector(`.list-placeholder[data-card-id="${cardId}"]`)
    if (!element) { return }
    const rect = element.getBoundingClientRect()
    return rect
  },
  listChildWidth (listWidth) {
    listWidth = listWidth || consts.minListWidth
    return listWidth - (consts.listPadding * 2)
  },

  // rect

  isPointInsideRect (point, rect, threshold = 0) {
    const xIsInside = this.isBetween({
      value: point.x,
      min: rect.x + threshold,
      max: rect.x + (rect.width || rect.resizeWidth) - threshold
    })
    const yIsInside = this.isBetween({
      value: point.y,
      min: rect.y + threshold,
      max: rect.y + rect.height - threshold
    })
    return xIsInside && yIsInside
  },
  isRectInsideViewport (rect) {
    const viewport = this.visualViewport()
    const viewportRect = {
      x: window.scrollX,
      y: window.scrollY,
      width: viewport.width,
      height: viewport.height
    }
    return this.isRectAInsideRectB(rect, viewportRect)
  },
  isNormalizedRectAInsideRectB (rectA, rectB) {
    // Check if any part of rectA intersects with rectB
    rectA.width = rectA.width || rectA.resizeWidth
    rectB.width = rectB.width || rectB.resizeWidth
    return (
      rectA.x < rectB.x + rectB.width &&
      rectA.x + rectA.width > rectB.x &&
      rectA.y < rectB.y + rectB.height &&
      rectA.y + rectA.height > rectB.y
    )
  },
  isRectAInsideRectB (rectA, rectB) {
    // udpate rects to support space zoom
    rectA = this.rectDimensions(rectA)
    rectB = this.rectDimensions(rectB)
    // Check if any part of rectA intersects with rectB
    return this.isNormalizedRectAInsideRectB(rectA, rectB)
  },
  isRectACompletelyInsideRectB (rectA, rectB) {
    // udpate rects to support space zoom
    rectA = this.rectDimensions(rectA)
    rectB = this.rectDimensions(rectB)
    // is rectA completely inside rectB
    return (
      rectA.x >= rectB.x &&
      rectA.y >= rectB.y &&
      (rectA.x + rectA.width) <= (rectB.x + rectB.width) &&
      (rectA.y + rectA.height) <= (rectB.y + rectB.height)
    )
  },
  boundaryRectFromItems (items) {
    items = this.clone(items)
    items = items.filter(item => item.x && item.y)
    items = items.map(item => {
      const defaultSize = 200
      item.width = item.resizeWidth || item.width || defaultSize
      item.height = item.resizeHeight || item.height || defaultSize
      return item
    })
    if (!items.length) {
      return { x: 0, y: 0, width: 0, height: 0 }
    }
    const rect = {}
    let xEnd = { x: 0, width: 0 }
    let yEnd = { y: 0, height: 0 }
    let xStart = { x: items[0].x }
    let yStart = { y: items[0].y }
    items.forEach(item => {
      const currentXEnd = xEnd.x + xEnd.width
      const currentYEnd = yEnd.y + yEnd.height
      const itemXEnd = item.x + item.width
      const itemYEnd = item.y + item.height
      if (itemXEnd > currentXEnd) { xEnd = item }
      if (itemYEnd > currentYEnd) { yEnd = item }
      if (item.x < xStart.x) { xStart = item }
      if (item.y < yStart.y) { yStart = item }
    })
    rect.x = xStart.x
    rect.width = xEnd.x + xEnd.width - xStart.x
    rect.y = yStart.y
    rect.height = yEnd.y + yEnd.height - yStart.y
    return rect
  },
  pageSizeFromItems (items) {
    const padding = 250
    const defaultSize = 200
    items = this.clone(items)
    items = items.filter(item => Number.isFinite(item.x) && Number.isFinite(item.y)) // 0 is valid value
    if (!items.length) {
      return { width: 0, height: 0 }
    }
    let width = 0
    let height = 0
    items.forEach(item => {
      let itemWidth = item.resizeWidth || item.width || defaultSize
      let itemHeight = item.resizeHeight || item.height || defaultSize
      itemWidth = item.x + itemWidth + padding
      itemHeight = item.y + itemHeight + padding
      if (itemWidth > width) {
        width = itemWidth
      }
      if (itemHeight > height) {
        height = itemHeight
      }
    })
    return { width, height }
  },
  async imageSize (url) {
    if (!url) { return }
    try {
      const image = new Image()
      const dimensions = await new Promise((resolve, reject) => {
        image.onload = () => resolve({
          width: image.width,
          height: image.height
        })
        image.onerror = () => reject(new Error('Failed to load image'))
        image.src = url
      })
      return dimensions
    } catch (error) {
      console.error('🚒 imageSize', url, error)
    }
  },
  mergeRectSizes (rect1, rect2) {
    if (!rect2) { return rect1 }
    return {
      width: Math.max(rect1.width, rect2.width),
      height: Math.max(rect1.height, rect2.height)
    }
  },

  // string

  nameStringFromItems (items) {
    items = items.filter(item => Boolean(item))
    const data = items.map(item => item.name)
    return join(data, '\n\n')
  },
  trim (string) {
    if (!string) { return '' }
    // https://regexr.com/59m7a
    // unlike string.trim(), this removes line breaks too
    return string.replace(/^(\n|\\n|\s)+|(\n|\\n|\s)+$/g, '')
  },
  hasBlankCharacters (string) {
    if (!string) { return true }
    // https://regexr.com/5i5a3
    // matches space, enter, tab, whitespace characters
    const blankPattern = new RegExp(/( |\s|\t)+/gm)
    if (string.match(blankPattern)) {
      return true
    }
  },
  splitByBlankCharacters (string) {
    // https://regexr.com/5i5a3
    // matches space, enter, tab, whitespace characters
    const blankPattern = new RegExp(/( |\s|\t)+/gm)
    return string.split(blankPattern)
  },

  // estimated card connector positions

  estimatedNewCardConnectorPosition (position) {
    if (!position) { return }
    const zoom = this.spaceCounterZoomDecimal()
    const offset = 15 * zoom
    return {
      x: position.x + consts.defaultCardWidth - offset,
      y: position.y + offset
    }
  },
  estimatedItemConnectorPosition (item) {
    const offset = 15
    if (item.itemType === 'box' && item.isLocked) {
      item.infoWidth = item.resizeWidth
    }
    const width = item.infoWidth || item.resizeWidth || item.width
    const rightSide = item.x + width
    let x = rightSide
    x = x - offset
    let y = item.y
    y = y + offset
    const position = {
      x: Math.round(x),
      y: Math.round(y)
    }
    return position
  },

  // Connections 🐙

  connectionElementFromId (connectionId) {
    return document.querySelector(`.connection[data-id="${connectionId}"]`)
  },
  migrateConnectionTypes (space) { // temp migration added april 2026
    if (!space.connectionTypes) { return space }
    space.connections = space.connections.filter(connection => Boolean(connection))
    space.connections = space.connections.map(connection => {
      const type = space.connectionTypes.find(type => type.id === connection?.connectionTypeId)
      const typeDataIsMissing = !connection.name && !connection.color
      if (type && typeDataIsMissing) {
        connection.name = type.name
        connection.color = type.color
      }
      return connection
    })
    return space
  },
  spaceZoomDecimal () {
    const element = document.getElementById('space')
    return element.dataset.zoom || 1
  },
  spaceCounterZoomDecimal () {
    return 1 / this.spaceZoomDecimal()
  },
  connectorCoords (itemId) {
    const itemConnector = document.querySelector(`.connector[data-item-id="${itemId}"] button`)
    const itemUnlockButton = document.querySelector(`.item-unlock-button[data-item-id="${itemId}"] button`)
    const element = itemConnector || itemUnlockButton
    if (!element) { return }
    const itemElement = this.itemElement(itemId)
    if (!itemElement.dataset.shouldRender) { return }
    const rect = element.getBoundingClientRect()
    rect.x = rect.x + window.scrollX
    rect.y = rect.y + window.scrollY
    const center = this.rectCenter(rect)
    return center
  },
  coordsWithCurrentScrollOffset ({ x, y, shouldIgnoreZoom }) {
    let zoom = this.spaceCounterZoomDecimal() || 1
    let spaceOffset = this.outsideSpaceOffset() || { x: 0, y: 0 }
    if (shouldIgnoreZoom) {
      zoom = 1
      spaceOffset = { x: 0, y: 0 }
    }
    x = (x + window.scrollX - spaceOffset.x) * zoom
    y = (y + window.scrollY - spaceOffset.y) * zoom
    return { x, y }
  },
  curveControlPointFromPath (path) {
    // https://regexr.com/8c6t6
    // matches 'q'-digits-,-digits-space: m295,284 q90,40 87,57 → "q90,40"
    const pathCoordsPattern = new RegExp(/q([-?\d.]{1,}),([-?\d.]{1,})/)
    let coords = path.match(pathCoordsPattern)
    coords = {
      x: coords[1],
      y: coords[2]
    }
    return this.integerCoords(coords)
  },
  boundingBoxFromPath (d) {
    // from https://stackoverflow.com/a/77749799
    // create temporary and hidden svg
    const ns = 'http://www.w3.org/2000/svg'
    const svg = document.createElementNS(ns, 'svg')
    const path = document.createElementNS(ns, 'path')
    svg.setAttribute('style', 'width:0!important; height:0!important; position:fixed!important; overflow:hidden!important; visibility:hidden!important; opacity:0!important')
    path.setAttribute('d', d)
    svg.append(path)
    document.body.append(svg)
    const bb = path.getBBox()
    // remove temporary svg
    svg.remove()
    return bb
  },
  startCoordsFromConnectionPath (path) {
    // https://regexr.com/66idp
    // matches first 2 digit groups in path: m295,284 q90,40 87,57 → [295, 284]
    const pathCoordsPattern = new RegExp(/m([\d.-]{1,}),([\d-.]{1,})/)
    let coords = path.match(pathCoordsPattern)
    if (!coords) { return }
    coords = {
      x: coords[1],
      y: coords[2]
    }
    return this.integerCoords(coords)
  },
  endCoordsFromConnectionPath (path) {
    // https://regexr.com/6mpru
    // matches end of string after the last space character: m295,284 q90,40 87,57 → "87,57"
    const endPathPattern = new RegExp(/([^ ]*$)/gm)
    const endPath = path.match(endPathPattern)[0]
    // https://regexr.com/6mpsj
    // split end path into coordinates: "87,57" → [87, 57]
    const coordsPattern = new RegExp(/([\d-.]*)/gm)
    let coords = endPath.match(coordsPattern)
    coords = coords.filter(value => Boolean(value))
    coords = {
      x: coords[0],
      y: coords[1]
    }
    return this.integerCoords(coords)
  },
  curveAxisBounds (controlPoint, end) {
    const values = [0, end] // [ start, end ]
    const denominator = end - (2 * controlPoint)
    if (denominator !== 0) {
      // position (0-1) along the curve where it turns back on this axis
      const position = -controlPoint / denominator
      // a curve that turns back between its end points is wider than they are
      if (position > 0 && position < 1) {
        // the curve at position, whose (1-position)² * start term is 0 because start is 0
        const controlPointAmount = 2 * (1 - position) * position * controlPoint
        const endAmount = position * position * end
        values.push(controlPointAmount + endAmount)
      }
    }
    return {
      min: Math.round(Math.min(...values)),
      max: Math.round(Math.max(...values))
    }
  },
  rectFromConnectionPath (path) {
    const pathStart = this.startCoordsFromConnectionPath(path)
    if (!pathStart) { return {} }
    const pathEndRelative = this.endCoordsFromConnectionPath(path)
    const controlPoint = this.curveControlPointFromPath(path)
    // the smallest rect containing the curve
    const x = this.curveAxisBounds(controlPoint.x, pathEndRelative.x)
    const y = this.curveAxisBounds(controlPoint.y, pathEndRelative.y)
    return {
      x: pathStart.x + x.min,
      y: pathStart.y + y.min,
      width: x.max - x.min,
      height: y.max - y.min
    }
  },
  labelRectFromConnectionPath (path) {
    const pathStart = this.startCoordsFromConnectionPath(path)
    if (!pathStart) { return {} }
    const pathEndRelative = this.endCoordsFromConnectionPath(path)
    const controlPoint = this.curveControlPointFromPath(path)
    // the middle of a quadratic curve, relative to the path start
    const middle = {
      x: (0.5 * controlPoint.x) + (0.25 * pathEndRelative.x),
      y: (0.5 * controlPoint.y) + (0.25 * pathEndRelative.y)
    }
    // wide enough for both end points to be inside the rect
    const width = 2 * Math.max(Math.abs(middle.x), Math.abs(pathEndRelative.x - middle.x))
    const height = 2 * Math.max(Math.abs(middle.y), Math.abs(pathEndRelative.y - middle.y))
    // a rect centered on the middle of a connection curve,
    // so that a label positioned at 0.5,0.5 sits on the curve
    return {
      x: Math.round(pathStart.x + middle.x - (width / 2)),
      y: Math.round(pathStart.y + middle.y - (height / 2)),
      width: Math.round(width),
      height: Math.round(height)
    }
  },
  rectFromDrawingStrokePath (path) {
    const coords = path.d.match(/[-\d.]+/g).map(Number)
    const xs = coords.filter((_, i) => i % 2 === 0)
    const ys = coords.filter((_, i) => i % 2 === 1)
    const minX = Math.min(...xs)
    const maxX = Math.max(...xs)
    const minY = Math.min(...ys)
    const maxY = Math.max(...ys)
    return {
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY
    }
  },
  integerCoords (coords) {
    return {
      x: parseInt(coords.x),
      y: parseInt(coords.y)
    }
  },
  pointOnCurve (pos, path) {
    // pos is 0 to 1
    const start = this.startCoordsFromConnectionPath(path)
    let end = this.endCoordsFromConnectionPath(path)
    end = {
      x: start.x + end.x,
      y: start.y + end.y
    }
    let cp = this.curveControlPointFromPath(path)
    cp = {
      x: start.x + cp.x,
      y: start.y + cp.y
    }
    // https://stackoverflow.com/questions/5634460/quadratic-bézier-curve-calculate-points
    let x = (1 - pos) * (1 - pos) * start.x + 2 * (1 - pos) * pos * cp.x + pos * pos * end.x
    let y = (1 - pos) * (1 - pos) * start.y + 2 * (1 - pos) * pos * cp.y + pos * pos * end.y
    x = Math.round(x)
    y = Math.round(y)
    return { x, y }
  },

  // Painting 🖌

  exponentialDecay (iteration, rateOfIterationDecay) {
    return Math.exp(-(rateOfIterationDecay * iteration))
  },
  filterCircles (circles, max) {
    max = max || 300
    const startIndex = circles.length - max
    if (startIndex > 0) {
      circles = circles.slice(startIndex, circles.length)
    }
    circles = circles.filter(circle => circle.iteration < max)
    return circles
  },
  easeOut (percentComplete, elaspedTime, duration) {
    const startValue = 0
    const endValue = 1
    return -endValue * (elaspedTime /= duration) * (elaspedTime - 2) + startValue
  },
  highestItemZ (items) {
    const highestZ = Math.max(...items.map(item => item.z || 0)) + 1
    return highestZ
  },

  // Spaces 🌙

  newSpaceName () {
    const date = dayjs(new Date())
    return date.format(consts.nameDateFormat)
  },
  // spaces can be in multiple groups
  spaceGroups (space) {
    if (!this.arrayHasItems(space?.groups)) { return [] }
    return space.groups.filter(group => Boolean(group?.id))
  },

  // sync

  isSyncItemUpdated (remoteItem, localItem) {
    let keys = Object.keys(remoteItem)
    keys = keys.filter(key => key !== 'updatedAt')
    return Boolean(keys.find(key => {
      const isChanged = localItem[key] !== remoteItem[key]
      if (isChanged) {
        return true
      }
    }))
  },
  syncItems (remoteItems, localItems) {
    const updateItems = []
    const addItems = []
    const removeItems = []
    remoteItems.forEach(remoteItem => {
      const localItem = localItems.find(item => item.id === remoteItem.id)
      // update changed items
      if (localItem) {
        if (this.isSyncItemUpdated(remoteItem, localItem)) {
          updateItems.push(remoteItem)
        }
      // add items not in local
      } else {
        addItems.push(remoteItem)
      }
    })
    // remove items not in remote
    localItems.forEach(localItem => {
      const remoteItem = remoteItems.find(item => item.id === localItem.id)
      if (!remoteItem) {
        removeItems.push(localItem)
      }
    })
    return { updateItems, addItems, removeItems }
  },
  newSpaceBackground (space, user) {
    if (user.defaultSpaceBackgroundGradient) {
      space.backgroundGradient = user.defaultSpaceBackgroundGradient
      space.backgroundIsGradient = true
    } else {
      space.background = user.defaultSpaceBackground
    }
    space.backgroundTint = user.defaultSpaceBackgroundTint
    return space
  },
  updateSpaceCardsCreatedThroughPublicApi (space) {
    space.cards = space.cards.map(card => {
      card.isCreatedThroughPublicApi = true
      return card
    })
    return space
  },
  emptySpace (spaceId) {
    return {
      id: spaceId,
      name: 'Loading…',
      background: '',
      backgroundTint: '',
      backgroundGradient: null,
      backgroundIsGradient: false,
      cards: [],
      connections: [],
      boxes: [],
      lines: [],
      lists: [],
      tags: [],
      users: [],
      userId: '',
      collaborators: [],
      spectators: [],
      clients: [],
      visits: 0,
      showInExplore: false,
      proposedShowInExplore: false,
      groups: [],
      note: '',
      userNewCardColor: null,
      tocItemsHidden: []
    }
  },
  deletePrivateSpaceMeta (space) {
    const spacePrivateKeys = [
      'clients',
      'spectators',
      'users',
      'collaborators',
      'collaboratorKey',
      'readOnlyKey'
    ]
    spacePrivateKeys.forEach(key => delete space[key])
    return space
  },
  resetSpaceMeta ({ space, user, type }) {
    space.originSpaceId = space.id
    space.id = nanoid()
    if (type) {
      space.name = `${space.name} ${type}`
    }
    space.removedCards = []
    space.users = [user]
    space.userId = user.id
    space.collaborators = []
    space.showInExplore = false
    space.proposedShowInExplore = false
    space.privacy = 'private'
    space.isTemplate = false
    space.previewImage = null
    space.previewThumbnailImage = null
    space.groups = []
    space.createdAt = new Date()
    space.editedAt = new Date().toISOString()
    space.collaboratorKey = nanoid()
    space.readOnlyKey = nanoid()
    space.cards = space.cards.map(card => {
      card.width = Math.ceil(card.width)
      card.height = Math.ceil(card.height)
      return card
    })
    space = this.updateSpaceItemsUserId(space, user.id)
    return space
  },
  excludeCurrentUser (users, currentUserId) {
    users = users.filter(user => user.id !== currentUserId)
    return users
  },
  itemUserId (user, item, nullItemUsers) {
    let userId
    if (nullItemUsers) {
      userId = null
    } else {
      userId = user.id || item.userId
    }
    return userId
  },
  removeOrphanedConnections (items, currentSpaceItemIds = []) {
    const { cards = [], boxes = [], lists = [], connections = [] } = items
    const itemIds = new Set([
      ...cards.map(card => card.id),
      ...boxes.map(box => box.id),
      ...lists.map(list => list.id),
      ...currentSpaceItemIds
    ])
    items.connections = connections.filter(connection => {
      return itemIds.has(connection.startItemId) && itemIds.has(connection.endItemId)
    })
    return items
  },
  async uniqueSpaceItems (items, nullItemUsers) {
    const itemIdDeltas = []
    const user = await cache.user()
    let {
      cards = [],
      connections = [],
      boxes = [],
      tags = [],
      lines = [],
      lists = [],
      drawingStrokes = []
    } = items
    cards = cards.map(card => {
      const userId = this.itemUserId(user, card, nullItemUsers)
      const newId = nanoid()
      itemIdDeltas.push({
        prevId: card.id,
        newId
      })
      card.id = newId
      card.userId = userId
      // update @mentions
      card.atUserMentions = card.atUserMentions || []
      card.atUserMentions = card.atUserMentions.map(value => {
        value.id = nanoid()
        value.cardId = newId
        return value
      })
      card.atDateMentions = card.atDateMentions || []
      card.atDateMentions = card.atDateMentions.map(value => {
        value.id = nanoid()
        value.cardId = newId
        return value
      })
      return card
    })
    boxes = boxes.map(box => {
      const userId = this.itemUserId(user, box, nullItemUsers)
      const newId = nanoid()
      itemIdDeltas.push({
        prevId: box.id,
        newId
      })
      box.id = newId
      box.userId = userId
      return box
    })
    lists = lists.map(list => {
      const userId = this.itemUserId(user, list, nullItemUsers)
      const newId = nanoid()
      itemIdDeltas.push({
        prevId: list.id,
        newId
      })
      list.id = newId
      list.userId = userId
      return list
    })
    lines = lines.map(line => {
      const userId = this.itemUserId(user, line, nullItemUsers)
      const newId = nanoid()
      line.id = newId
      line.userId = userId
      return line
    })
    drawingStrokes = drawingStrokes.map(strokes => {
      strokes = strokes.map(point => {
        const userId = this.itemUserId(user, point, nullItemUsers)
        const newId = nanoid()
        point.id = newId
        point.userId = userId
        return point
      })
      return strokes
    })
    connections = connections.map(connection => {
      const userId = this.itemUserId(user, connection, nullItemUsers)
      connection.id = nanoid()
      connection.startItemId = this.updateAllIds(connection, 'startItemId', itemIdDeltas)
      connection.endItemId = this.updateAllIds(connection, 'endItemId', itemIdDeltas)
      connection.userId = userId
      return connection
    })
    tags = tags.map(tag => {
      const userId = this.itemUserId(user, tag, nullItemUsers)
      tag.id = nanoid()
      tag.cardId = this.updateAllIds(tag, 'cardId', itemIdDeltas)
      tag.userId = userId
      return tag
    })
    cards = cards.map(card => {
      card.listId = this.updateAllIds(card, 'listId', itemIdDeltas)
      return card
    })
    items = { cards, connections, boxes, tags, lines, drawingStrokes, lists }
    return items
  },
  updateSpaceItemsSpaceId (items, spaceId) {
    const keys = Object.keys(items)
    keys.forEach(key => {
      items[key] = this.updateItemsSpaceId(items[key], spaceId)
    })
    return items
  },
  updateItemsSpaceId (items, spaceId) {
    return items.map(item => {
      item.spaceId = spaceId
      return item
    })
  },
  updateSpaceItemsRelativeToOrigin (items) {
    items = this.clone(items)
    // offset
    const positionItems = []
    consts.itemTypesWithPositions.forEach(itemName => {
      items[itemName].forEach(item => positionItems.push(item))
    })
    const offset = this.topLeftItem(positionItems)
    console.info(positionItems, offset)
    // update positions
    consts.itemTypesWithPositions.forEach(itemName => {
      items[itemName] = items[itemName].map(item => {
        item.x = item.x - offset.x
        item.y = item.y - offset.y
        return item
      })
    })
    return items
  },
  updateSpaceItemsAddPosition (items, position) {
    items = this.clone(items)
    consts.itemTypesWithPositions.forEach(itemName => {
      items[itemName] = items[itemName].map(item => {
        item.x = item.x + position.x
        item.y = item.y + position.y
        item.x = Math.max(consts.minItemXY, item.x)
        item.y = Math.max(consts.minItemXY, item.y)
        return item
      })
    })
    return items
  },
  updateSpaceItemsUserId (space, userId) {
    consts.itemTypes.forEach(itemType => {
      if (!space[itemType]) { return }
      space[itemType] = space[itemType].map(item => {
        const shouldUpdate = item.userId !== null && item.userId !== consts.rootUserId // ensures corect free card count
        if (shouldUpdate) {
          item.userId = userId
          item.nameUpdatedByUserId = null
        }
        return item
      })
    })
    return space
  },
  updateSpacesUser (user, spaces) {
    spaces = spaces.map(space => {
      space = this.updateSpaceItemsUserId(space, user.id)
      // space = this.updateSpaceUserId(space, userId)
      space.userId = user.id
      space.editedByUserId = user.id
      space.users = [user]
      return space
    })
    return spaces
  },
  newHelloSpace (user) {
    const emptyStringKeys = ['id', 'collaboratorKey', 'readOnlyKey', 'note']
    const emptyArrayKeys = ['users', 'collaborators', 'spectators', 'clients']
    const deleteKeys = ['url', 'originSpaceId', 'editedAt', 'editedByUserId', 'createdAt', 'updatedAt', 'updateHash']
    const userId = user?.id || consts.moderatorUserId
    const space = this.clone(helloSpace)
    space.name = 'Hello Kinopio'
    space.privacy = 'private'
    space.visits = 0
    space.showInExplore = false
    space.isTemplate = false
    space.showInExploreUpdatedAt = null
    emptyStringKeys.forEach(key => {
      space[key] = ''
    })
    emptyArrayKeys.forEach(key => {
      space[key] = []
    })
    deleteKeys.forEach(key => {
      delete space[key]
    })
    this.updateSpaceItemsUserId(space, userId)
    space.userId = userId
    return space
  },
  normalizeSpace (space) {
    if (!this.objectHasKeys(space)) { return space }
    if (!this.arrayHasItems(space.connections)) { return space }
    space.connections = space.connections || []
    space.cards = space.cards || []
    space.cards = space.cards.filter(card => card?.name) || []
    space.cards = space.cards.map(card => {
      if (card.resizeWidth) {
        card.resizeWidth = Math.round(card.resizeWidth)
      }
      return card
    })
    space = this.migrateConnectionTypes(space)
    return space
  },
  normalizeRemoteSpace (remoteSpace) {
    const removedCards = []
    const cards = []
    remoteSpace.cards.forEach(card => {
      if (card.isRemoved) {
        removedCards.push(card)
      } else {
        cards.push(card)
      }
    })
    remoteSpace.cards = cards
    remoteSpace.removedCards = removedCards
    return remoteSpace
  },
  addCurrentUserIsCollaboratorToSpaces (spaces, currentUser) {
    if (!spaces) { return }
    return spaces.map(space => {
      let userId
      space.users = space.users || []
      if (space.users.length) {
        userId = space.users[0].id
      }
      userId = userId || space.userId
      if (userId !== currentUser.id) {
        space.currentUserIsCollaborator = true
      }
      return space
    })
  },
  removeRemovedCardsFromSpace (space) {
    if (!space.cards) { return }
    const cards = []
    space.cards.forEach(card => {
      if (!card) {
        return
      }
      if (!card.isRemoved) {
        cards.push(card)
      }
    })
    space.cards = cards
    return space
  },
  spaceReadDate (space, type) {
    this.typeCheck({ value: space, type: 'object' })
    this.typeCheck({ value: type, type: 'string' }) // 'explore', 'following', 'everyone'
    let date
    if (type === 'explore') {
      date = space.showInExploreUpdatedAt
    } else if (type === 'following') {
      date = space.updatedAt
    } else if (type === 'everyone') {
      date = space.createdAt
    }
    return dayjs(date)
  },

  // urls 🌍

  // same as server util
  normalizeString (string) {
    if (!string) { return }
    // remove punctuation characters, what's → whats
    string = string.replace(/'|"|‘|’|“|”/ig, '')
    // replaces non alphanumeric (spaces, emojis, $%&, etc.) characters with '-'s
    string = string.replace(/([^a-z0-9-]+)/ig, '-').toLowerCase()
    // replace carriage returns with new lines
    string = string.replace(/\r\n/g, '\n')
    return string
  },
  removeLeadingDashes (string) {
    // -123-abc -> 123-abc
    return string.replace(/^-+/, '')
  },
  normalizeFileUrl (string) {
    // same as normalizeString^, but keeps '.' and case
    return string.replace(/([^a-z0-9-.]+)/ig, '-')
  },
  // same as server util
  url ({ name, id }) {
    if (name) {
      const normalizedName = this.normalizeString(name)
      return `${normalizedName}-${id}`
    } else {
      return id
    }
  },
  newUrl (url) {
    try {
      return new URL(url)
    } catch (error) {
      return undefined
    }
  },
  spaceUrl ({ spaceId, spaceName, collaboratorKey, readOnlyKey }) {
    let url
    if (collaboratorKey || readOnlyKey) {
      url = this.spaceInviteUrl({ spaceId, spaceName, collaboratorKey, readOnlyKey })
    } else {
      url = this.url({ name: spaceName, id: spaceId })
    }
    return url
  },
  urlFromSpaceAndItem ({ spaceId, itemId }) {
    let url = `${consts.kinopioDomain()}/${spaceId}`
    if (itemId) {
      url = `${url}/${itemId}`
    }
    return url
  },
  spaceInviteUrl ({ spaceId, spaceName, collaboratorKey, readOnlyKey, isCommentMode }) {
    if (!spaceId) { return }
    spaceName = this.normalizeString(spaceName)
    spaceName = this.truncated(spaceName, 20, '-')
    let inviteQuery = ''
    if (collaboratorKey) {
      inviteQuery = `collaboratorKey=${collaboratorKey}`
    } else if (readOnlyKey) {
      inviteQuery = `readOnlyKey=${readOnlyKey}`
    }
    let url = `${consts.kinopioDomain()}/space/invite/${spaceId}?${inviteQuery}&name=${spaceName}`
    if (isCommentMode) {
      url = url + '&comment=true'
    }
    return url
  },
  groupInviteUrl ({ groupId, groupName, collaboratorKey }) {
    if (!groupId || !collaboratorKey) { return }
    groupName = this.normalizeString(groupName)
    groupName = this.truncated(groupName, 20, '-')
    const inviteQuery = `collaboratorKey=${collaboratorKey}`
    const url = `${consts.kinopioDomain()}/group/invite/${groupId}?${inviteQuery}&name=${groupName}`
    return url
  },
  urlSearchParamsToObject (searchParams) {
    const object = {}
    for (const [key, value] of searchParams.entries()) {
      object[key] = value
    }
    return object
  },
  groupFromGroupInviteUrl (url) {
    if (!url) { return }
    url = new URL(url)
    const segments = url.pathname.split('/').filter(Boolean)
    const group = this.urlSearchParamsToObject(url.searchParams)
    group.id = segments[2]
    return group
  },
  spaceAndCardIdFromUrl (url) {
    url = new URL(url)
    const path = url.pathname
    // https://regexr.com/5kr4g
    // matches (text after /) twice
    const urlPattern = new RegExp(/\/([^?\s/]+)\/{0,1}([^?\s/]+){0,1}/i)
    let matches = path.match(urlPattern)
    if (!matches) { return }
    const spaceUrl = matches[1]
    const spaceId = spaceUrl.substring(spaceUrl.length - uuidLength, spaceUrl.length)
    matches = {
      spaceUrl,
      cardId: matches[2],
      spaceId
    }
    return matches
  },
  spaceIdFromUrl (url) {
    url = url || window.location.href
    url = this.newUrl(url)
    if (!url) { return }
    const path = url.pathname
    const id = path.substring(path.length - uuidLength, path.length)
    if (this.idIsValid(id)) { return id }
  },
  idIsValid (id) {
    if (!id) { return }
    if (id.includes('/')) {
      return undefined
    }
    return true
  },
  normalizeUrl (url) {
    const lastCharacterPosition = url.length - 1
    if (url[lastCharacterPosition] === '/') {
      return url.slice(0, lastCharacterPosition)
    }
    return url
  },
  urlIsRetina (url) {
    const isRetina = url.includes('-2x.') || url.includes('@2x.')
    return isRetina
  },
  urlIsFloatOrIp (url) {
    // https://regexr.com/58ii6
    // matches numbers '.'' numbers ...
    const floatOrIpPattern = new RegExp(/^(?:[0-9]+\.)+[0-9]+$/igm)
    if (url.match(floatOrIpPattern)) {
      return true
    }
  },
  urlIsCurrencyFloat (url) {
    // https://regexr.com/5bfgm
    // matches currencySymbol numbers '.' numbers
    const currencyFloatPattern = new RegExp(/^[$€£₾₺₴₦₪R¥元₹₱₩฿₫₿ɱŁΞ]{1}[0-9]+\.[0-9]+/g)
    if (url.match(currencyFloatPattern)) {
      return true
    }
  },
  hostIsKinopio (url) {
    url = new URL(url)
    return url.origin === consts.kinopioDomain()
  },
  urlIsValidLocalhost (url) {
    // https://regexr.com/7vc0o
    // matches ':' then numbers
    // then ends or continues with '/' or '?'
    const portPattern = new RegExp(/(:[0-9]+)(\?|\/(\w+|\d+)| |$)/igm)
    if (url.match(portPattern)) {
      return true
    }
  },
  urlIsValidTld (url) {
    if (!url) { return }
    const isLocalhostUrl = url.match(this.localhostUrlPattern())
    const isDevelopmentUrl = url.includes(consts.kinopioDomain())
    if (isLocalhostUrl || isDevelopmentUrl) { return true }
    // https://regexr.com/5v6s9
    const regex = '(' + tlds + ')' + String.raw`(\?|\/| |$|\s)`
    const tldPattern = new RegExp(regex)
    url = url.toLowerCase()
    if (url.match(tldPattern)) {
      return true
    }
  },
  urlFromString (string) {
    if (!string) { return }
    const urls = this.urlsFromString(string)
    if (urls) {
      return urls[0]
    } else {
      return null
    }
  },
  urlFromCSSBackgroundImage (string) {
    if (!string) { return }
    // https://regexr.com/5vvc4
    // image from `url("image")`
    const urlPattern = new RegExp(/(http[s]?:\/\/)[^\s(["<>]{2,}\.[^\s.[">,<]+\w\/?/igm)
    const urls = string.match(urlPattern)
    return urls[0]
  },
  localhostUrlPattern () {
    // https://regexr.com/6cujp
    // start, newline, or space
    // http://localhost:
    // then port numbers
    // then the rest of the url path
    return new RegExp(/(^|\n| )(http:\/\/localhost:)[^\s."><]+\w\/?-?/igm)
  },
  emailsFromString (string) {
    // https://stackoverflow.com/a/42408099
    return string.match(/([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/gim) || []
  },
  emailIsValid (string) {
    return Boolean(this.emailsFromString(string).length)
  },
  urlsFromString (string) {
    if (!string) { return [] }
    // remove markdown links
    const markdownLinks = string.match(this.markdown().linkPattern)
    if (markdownLinks) {
      markdownLinks.forEach(link => {
        string = string.replace(link, '')
      })
    }
    string = this.removeMarkdownCodeblocksFromString(string)
    // matches multiple urls and returns [urls]
    // https://regexr.com/59m5t
    // start, newline, or space
    // starts with http/s protocol
    // followed by alphanumerics
    // then '.', or ':' + portnumbers
    // followed by at least 1 alphanumeric, '=', or '.'
    // then optional trailing '/' or '-'
    const urlPattern = new RegExp(/(^|\n| )(http[s]?:\/\/)[^\s(["<>]{1,}(\.|(:[0-9]+))[^\s."><]+[\w=.!)]+\/?-?/igm)
    const localhostUrls = string.match(this.localhostUrlPattern()) || []
    let urls = string.match(urlPattern) || []
    urls = urls.concat(localhostUrls)
    urls = urls.filter(url => Boolean(url))
    if (!urls.length) { return }
    // filter out empty or non-urls
    urls = urls.map(url => this.trim(url))
    urls = urls.filter(url => {
      if (!url) { return }
      // const urlIsMarkdownEmphasis = Boolean(this.markdown().emphasisPattern2.exec(url))
      const isInvalidUrl = this.urlIsFloatOrIp(url) || this.urlIsCurrencyFloat(url)
      if (!isInvalidUrl) {
        return true
      }
    })
    // ensure url has protocol
    urls = urls.map(url => {
      const isFile = this.urlIsFile(url)
      const hasProtocol = this.urlHasProtocol(url)
      if (isFile || hasProtocol) {
        return url
      }
    })
    return urls
  },
  urlHasProtocol (url) {
    if (!url) { return }
    url = url.toLowerCase()
    return url.startsWith('http://') || url.startsWith('https://')
  },
  urlWithoutProtocol (url) {
    let newUrl
    const http = 'http://'
    const https = 'https://'
    if (url.match(http)) {
      newUrl = url.replace(http, '')
    } else if (url.match(https)) {
      newUrl = url.replace(https, '')
    } else {
      newUrl = url
    }
    return newUrl
  },
  urlWithProtocol (url) {
    if (!url) { return }
    if (this.urlHasProtocol(url)) { return url }
    return `https://${url}`
  },
  urlIsImage (url) {
    if (!url) { return }
    // append space to match as an end character
    url = url + ' '
    // https://regexr.com/6dv93
    // matches wikipedia.org/wiki/ABC_123#/media/File
    const wikipediaMediaPagePattern = new RegExp(/wikipedia.org\/wiki\/[A-Zaz_0-9:]+#\/media\/File/igm)
    const isPage = url.match(wikipediaMediaPagePattern)
    if (isPage) { return }
    // https://regexr.com/4rjtu
    // match an extension
    // which much be followed by either end of line, space, or ? (for qs) char
    const imageUrlPattern = new RegExp(/(?:\.gif|\.jpg|\.jpeg|\.jpe|\.jif|\.jfif|\.png|\.svg|\.webp|\.avif|\.heic)(?:\n| |\?|&)/igm)
    const isImage = url.match(imageUrlPattern) || url.includes('is-image=true')
    return Boolean(isImage)
  },
  imageFileTypeFromName (file) {
    // https://regexr.com/8ggu1
    // based on imageUrlPattern
    // starts with a word boundary, no trailing characters
    const imageNamePattern = new RegExp(/(\b)(?:\.gif|\.jpg|\.jpeg|\.jpe|\.jif|\.jfif|\.png|\.svg|\.webp|\.avif|\.heic)/igm)
    const match = file.name.match(imageNamePattern)
    if (!match) { return }
    const fileType = match[0].replace('.', 'image/')
    return fileType
  },
  urlIsVideo (url) {
    if (!url) { return }
    url = url + ' '
    const videoUrlPattern = new RegExp(/(?:\.mp4|\.webm|\.avif|\.mov)(?:\n| |\?|&)/igm)
    const isVideo = url.match(videoUrlPattern)
    return Boolean(isVideo)
  },
  urlIsAudio (url) {
    if (!url) { return }
    url = url + ' '
    const audioUrlPattern = new RegExp(/(?:\.mp3|\.m4a|\.ogg)(?:\n| |\?|&)/igm)
    const isAudio = url.match(audioUrlPattern)
    return Boolean(isAudio)
  },
  urlIsFile (url) {
    if (!url) { return }
    url = url + ' '
    const fileUrlPattern = new RegExp(/(?:\.txt|\.md|\.markdown|\.pdf|\.log|\.ppt|\.pptx|\.doc|\.docx|\.csv|\.xls|\.xlsx|\.rtf|\.zip|\.tar|\.xml|\.psd|\.ai|\.ind|\.sketch|\.mov|\.7z|\.woff|\.woff2|\.otf|\.ttf|\.wav|\.flac\.pla\.json)(?:\n| |\?|&)/igm)
    const isFile = url.toLowerCase().match(fileUrlPattern)
    return Boolean(isFile)
  },
  urlIsSpaceInvite (url) {
    url = this.urlWithProtocol(url)
    if (!url) { return }
    try {
      url = new URL(url)
      return url.pathname.startsWith('/space/invite/')
    } catch (error) {
      console.warn('🚑 urlIsSpaceInvite', error)
    }
  },
  urlIsGroupInvite (url) {
    const hostIsKinopio = this.hostIsKinopio(url)
    if (!hostIsKinopio) { return }
    url = new URL(url)
    return url.pathname.startsWith('/group/invite/')
  },
  urlIsApp (url) {
    return Boolean(this.urlIsGroupInvite(url) || this.urlIsSpaceInvite(url) || this.urlIsSpace(url))
  },
  urlIsSpace (url) {
    if (!url) { return }
    if (this.urlIsGroupInvite(url)) { return }
    if (this.urlIsSpaceInvite(url)) { return }
    let spaceUrlPattern
    if (consts.isDevelopment()) {
      // https://regexr.com/5hjc2
      spaceUrlPattern = new RegExp(/(?:kinopio\.local:.*\/)(.*)\b/gi)
    } else {
      // https://regexr.com/60jvc
      // 'https://kinopio.club/' (protocol required)
      // no 'invite?' after 'club' (no invite links)
      // alphanumber and '-' characters after
      // until whitespace or end of string
      spaceUrlPattern = new RegExp(/(https:\/\/kinopio\.club\/)(?:(?!invite\?).)(['A-Za-z0-9-]*)/gi)
    }
    const isSpaceUrl = url.match(spaceUrlPattern)
    if (!isSpaceUrl) { return }
    if (this.urlIsKinopioRootOrStaticPage(url)) { return }
    return true
  },
  urlIsKinopioRootOrStaticPage (url) {
    url = this.newUrl(url)
    if (!url) { return }
    const path = url.pathname.split('/')[1]
    if (!path) { return true }
    return consts.staticPagePaths.includes(path.toLowerCase())
  },
  urlIsWebsite (url) {
    const isImage = this.urlIsImage(url)
    const isVideo = this.urlIsVideo(url)
    const isAudio = this.urlIsAudio(url)
    const isFile = this.urlIsFile(url)
    const isSpace = this.urlIsSpace(url)
    return !isImage && !isVideo && !isAudio && !isFile && !isSpace
  },
  urlIsYoutube (url) {
    if (url.includes('/channel/')) { return }
    const domains = ['https://youtube.com', 'https://www.youtube.com', 'https://m.youtube.com', 'https://youtu.be']
    let isRoot, isVideo
    domains.forEach(domain => {
      if (url === domain) { isRoot = true }
      if (url === domain + '/') { isRoot = true }
    })
    if (isRoot) { return }
    domains.forEach(domain => {
      if (url.includes(domain)) { isVideo = true }
    })
    return isVideo
  },
  urlIsSpotify (url) {
    return url.includes('https://open.spotify.com')
  },
  fileNameFromUrl (url) {
    if (!url) { return }
    // https://regexr.com/4rjtu
    // /filename.pdf from end of string
    const filePattern = new RegExp(/\/[A-z0-9-]+\.[A-z.0-9-]+(\?[A-z.0-9-=]*)*$/gim)
    let file = url.match(filePattern)

    if (!file) { return }
    file = file[0]
    // remove leading '/''
    const name = file.substring(1, file.length)
    return name
  },
  urlWithoutQueryString (url) {
    url = this.urlWithoutTrailingSlash(url)
    return url.split('?')[0]
  },
  urlWithoutTrailingSlash (url) {
    const lastCharacter = url.substring(url.length - 1, url.length)
    if (lastCharacter === '/') {
      url = url.substring(0, url.length - 1)
    }
    return url
  },
  queryString (url) {
    const split = url.split('?')
    if (split.length <= 1) {
      return undefined
    } else {
      const splitFragment = split[1].split('#')
      if (split.length <= 1) {
        return split[1]
      } else {
        return splitFragment[0]
      }
    }
  },
  urlType (url) {
    if (!url) { return }
    if (this.urlIsImage(url)) {
      return 'image'
    } else if (this.urlIsVideo(url)) {
      return 'video'
    } else if (this.urlIsAudio(url)) {
      return 'audio'
    } else {
      return 'link'
    }
  },
  clearTrackingQueryStringsFromUrls (name) {
    const urls = this.urlsFromString(name)
    // https://www.bleepingcomputer.com/PoC/qs.html
    // https://www.bleepingcomputer.com/news/security/new-firefox-privacy-feature-strips-urls-of-tracking-parameters
    const trackingKeys = ['is_copy_url', 'is_from_webapp', 'utm_', 'oly_enc_id', 'oly_anon_id', '__s', 'vero_id', '_hsenc', 'mkt_tok', 'fbclid', 'mc_eid', 'pf_', 'pd_']
    urls.forEach(url => {
      url = url.trim()
      url = this.clearTrailingSlash(url)
      const queryString = this.queryString(url)
      const domain = this.urlWithoutQueryString(url)
      if (!queryString) { return }
      const queryObject = qs.decode(queryString)
      const keys = Object.keys(queryObject)
      const keysToRemove = []
      trackingKeys.forEach(trackingKey => {
        keys.forEach(key => {
          if (key.startsWith(trackingKey)) {
            keysToRemove.push(key)
          }
        })
      })
      if (!keysToRemove.length) { return }
      console.info('🫧 trackingKeysToRemove', keysToRemove)
      keysToRemove.forEach(key => delete queryObject[key])
      const newUrl = qs.encode(domain, queryObject)
      name = name.replace(url, newUrl)
    })
    return name
  },

  // Checkbox ✅

  nameIsUnchecked (name) {
    if (!name) { return }
    // https://regexr.com/55afe
    // matches [·]· at the start of a string, with optional ·space inside []s
    const taskPattern = new RegExp(/^(\[(\ )?\]\ )/g) // eslint-disable-line no-useless-escape
    const isTask = name.match(taskPattern)
    return Boolean(isTask)
  },
  nameIsChecked (name) {
    if (!name) { return }
    // https://regexr.com/55afk
    // matches [x] at the start of a string
    const taskPattern = new RegExp(/^(\[[xX]\]\ )/g) // eslint-disable-line no-useless-escape
    const isTask = name.match(taskPattern)
    return Boolean(isTask)
  },
  checkboxFromString (name) {
    if (!name) { return }
    let match
    const doing = new RegExp(/^(\[(\ )?\]\ )/g) // eslint-disable-line no-useless-escape
    match = name.match(doing)
    if (match) { return match[0] }
    const done = new RegExp(/^(\[[xX]\]\ )/g) // eslint-disable-line no-useless-escape
    match = name.match(done)
    if (match) { return match[0] }
  },
  updateCheckboxItemName (item, value) {
    let name = item.name
    const checkbox = this.checkboxFromString(name)
    name = name.replace(checkbox, '')
    if (value) {
      name = `[x] ${name}`
    } else {
      name = `[] ${name}`
    }
    item.name = name
    return item
  },

  // Paste Card ✂️

  // recursive
  uniqueCardPosition (point, existingPoints) {
    const isSamePosition = existingPoints.filter(existingPoint => {
      return existingPoint.x === point.x && existingPoint.y === point.y
    })
    if (isSamePosition.length) {
      point.x += 20
      point.y += 20
      return this.uniqueCardPosition(point, existingPoints)
    } else {
      return point
    }
  },

  // Broadcast Websocket 🌝

  userMeta (user, space) {
    const isUser = space.users.find(spaceUser => {
      return spaceUser.id === user.id
    })
    const spaceCollaborators = space.collaborators || []
    const isCollaborator = spaceCollaborators.find(collaborator => {
      return collaborator.id === user.id
    })
    const isSpectator = !(isUser || isCollaborator)
    const isSignedIn = Boolean(user.apiKey)
    return {
      id: user.id,
      name: user.name,
      color: user.color,
      description: user.description,
      website: user.website,
      isUpgraded: user.isUpgraded,
      isModerator: user.isModerator,
      isDonor: user.isDonor,
      isSignedIn,
      isSpectator,
      isCollaborator
    }
  },
  spaceMeta (space) {
    return {
      id: space.id,
      name: space.name,
      privacy: space.privacy,
      previewThumbnailImage: space.previewThumbnailImage,
      showInExplore: space.showInExplore
    }
  },

  // Upload

  isFileTooBig ({ file, userIsUpgraded }) {
    let sizeLimit = 1024 * 1024 * consts.freeUploadSizeLimit // 5mb
    if (userIsUpgraded) {
      sizeLimit = 1024 * 1024 * consts.upgradedUploadSizeLimit // 256
    }
    return file.size > sizeLimit
  },
  async dataFromClipboard () {
    let text, file
    const items = await navigator.clipboard.read()
    console.info('🎊 dataFromClipboard', items)
    for (const item of items) {
      const imageMatch = 'image/'
      const imageType = item.types.find(type => {
        if (!type) { return }
        return type.includes(imageMatch)
      })
      if (imageType) {
        const blob = await item.getType(imageType)
        let index = imageType.indexOf(imageMatch)
        index = index + imageMatch.length
        const extension = imageType.substring(index)
        file = new File([blob], `pasted.${extension}`)
      } else {
        if (item.types.includes('text/plain')) {
          const blob = await item.getType('text/plain')
          text = await blob.text()
        }
      }
    }
    if (text == null && file == null) {
      text = await navigator.clipboard.readText()
    }
    return { text, file }
  },
  // for canvas testing
  downloadBlob (blob) {
    const URLObj = window.URL || window.webkitURL
    const a = document.createElement('a')
    a.href = URLObj.createObjectURL(blob)
    a.download = 'untitled.png'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  },
  // for canvas testing
  downloadCanvasImage (dataUrl, filename = 'image.png') {
    const link = document.createElement('a')
    link.href = dataUrl
    link.download = filename
    link.click()
  },

  // Tags 🦋

  tagsFromString (string) {
    if (!string) { return }
    // remove `code blocks`
    const codeBlocks = string.match(this.markdown().codeBlockPattern) || string.match(this.markdown().codePattern)
    if (codeBlocks) {
      codeBlocks.forEach(code => {
        string = string.replace(code, '')
      })
    }
    // https://regexr.com/5bv6b
    // '[' twice
    // then anything except line break and ']'
    // ']' twice
    const tagPattern = new RegExp(/([[]{2}[^\n(\]\])]+[\]]{2})/gm)
    const tags = string.match(tagPattern)
    return tags
  },
  tagsFromStringWithoutBrackets (string) {
    let tags = this.tagsFromString(string)
    if (!tags) { return }
    tags = tags.map(tag => tag.substring(2, tag.length - 2))
    return tags
  },
  indexesOf (string, search) {
    // adapted from https://stackoverflow.com/a/3410549
    search = search.replaceAll('[', '\\[')
    const prevSearch = search
    search = search.replaceAll('?', '\\?')
    search = search.replaceAll('$', '\\$')
    search = search.replaceAll('+', '\\+')
    const extraCharacters = 2 + (search.length - prevSearch.length)
    const searchPattern = new RegExp(search, 'gim')
    const results = []
    while (searchPattern.exec(string)) {
      const position = searchPattern.lastIndex - search.length + extraCharacters
      results.push(position)
    }
    return results
  },
  tagStyle (tag) {
    const styles = { backgroundColor: tag.color }
    const isDark = this.colorIsDark(tag.color)
    if (isDark) {
      const color = this.cssVariable('primary')
      styles.color = this.invertColor(color)
    }
    return styles
  },

  // App Buttons

  commandsFromString (string) {
    const allowedCommands = Object.keys(consts.systemCommands)
    // https://regexr.com/7h3ia
    const commandPattern = new RegExp(/::systemCommand=\w+/gm)
    let commands = string.match(commandPattern)
    if (!commands) { return }
    commands = commands.filter(command => {
      const name = this.commandNameFromCommand(command)
      return allowedCommands.includes(name)
    })
    return commands
  },
  commandNameFromCommand (string) {
    // https://regexr.com/7h3ig
    // ::system_command=xyz → matches xyz
    const commandNamePattern = new RegExp(/=\w+/gm)
    let name = string.match(commandNamePattern)
    name = name[0]
    name = name.replace('=', '')
    return name
  },

  // Name Segments 🎫

  segmentsWithTextSegments (name, segments) {
    let currentStart = 0
    const textSegments = []
    segments = sortBy(segments, ['startPosition'])
    segments.forEach(({ startPosition, endPosition }) => {
      if (currentStart < startPosition) {
        const start = currentStart
        const end = startPosition
        const content = name.substring(start, end)
        textSegments.push({ startPosition: start, endPosition: end, content, isText: true })
      }
      currentStart = endPosition
    })
    // handle trailing text
    if (currentStart < name.length) {
      const start = currentStart
      const end = name.length
      const content = name.substring(start, end)
      textSegments.push({ startPosition: start, endPosition: end, content, isText: true })
    }
    segments = segments.concat(textSegments)
    segments = sortBy(segments, ['startPosition'])
    return segments
  },
  cardNameSegments (name, atMentions) {
    if (!name) { return [] }
    atMentions = atMentions || []
    const tags = this.tagsFromString(name) || []
    const urls = this.urlsFromString(name) || []
    const commands = this.commandsFromString(name) || []
    const markdownLinks = name.match(this.markdown().linkPattern) || []
    const links = urls.filter(url => {
      const linkIsMarkdown = markdownLinks.find(markdownLink => markdownLink.includes(url))
      if (linkIsMarkdown) { return }
      return this.urlIsSpace(url) || this.urlIsSpaceInvite(url) || this.urlIsGroupInvite(url)
    })
    const files = urls.filter(url => this.urlIsFile(url))
    let segments = []
    tags.forEach(tag => {
      // remove previous duplicate tag names
      let startPositions = this.indexesOf(name, tag)
      segments.forEach(segment => {
        startPositions = startPositions.filter(position => position > segment.startPosition)
      })
      const startPosition = startPositions[0]
      const endPosition = startPosition + tag.length
      segments.push({ tag, startPosition, endPosition, name: tag.substring(2, tag.length - 2), isTag: true })
    })
    links.forEach(link => {
      const startPosition = name.indexOf(link)
      const endPosition = startPosition + link.length
      const { spaceId, spaceUrl, cardId } = this.spaceAndCardIdFromUrl(link)
      const segment = { link, name: link, startPosition, endPosition, spaceId, spaceUrl, cardId, isSpaceLink: true }
      if (this.urlIsSpaceInvite(link)) {
        segment.isInviteLink = true
        const url = new URL(link)
        const queryObject = qs.decode(url.search)
        segment.spaceId = queryObject.spaceId
        segment.collaboratorKey = queryObject.collaboratorKey
      }
      segments.push(segment)
    })
    files.forEach(file => {
      const startPosition = name.indexOf(file)
      const endPosition = startPosition + file.length
      segments.push({ file, startPosition, endPosition, name: this.fileNameFromUrl(file), isFile: true })
    })
    commands.forEach(command => {
      const startPosition = name.indexOf(command)
      const endPosition = startPosition + command.length
      const commandName = this.commandNameFromCommand(command)
      segments.push({ startPosition, endPosition, command: commandName, name: consts.systemCommands[commandName], isCommand: true })
    })
    atMentions.forEach(mention => {
      if (!mention.stringMatch) { return }
      const startPositions = []
      let index = name.indexOf(mention.stringMatch)
      while (index !== -1) {
        startPositions.push(index)
        index = name.indexOf(mention.stringMatch, index + mention.stringMatch.length)
      }
      // use the first position not already covered by another segment
      const startPosition = startPositions.find(position => {
        const isUsed = segments.find(segment => position >= segment.startPosition && position < segment.endPosition)
        return !isUsed
      })
      if (startPosition === undefined) { return }
      const endPosition = startPosition + mention.stringMatch.length
      // date mentions have a date property, user mentions have a userId
      const isAtDateMention = Boolean(mention.date)
      segments.push({
        startPosition,
        endPosition,
        name: mention.stringMatch,
        userId: mention.userId,
        date: mention.date,
        isAtDateMention,
        isAtUserMention: !isAtDateMention
      })
    })
    segments = this.segmentsWithTextSegments(name, segments)
    return segments
  },
  markdown () {
    return {
      // https://regexr.com/5jmf1
      // matches [text](url)
      linkPattern: /\[([^[]+)\]\(([^\n ]+)\)/gmi,
      // https://regexr.com/5jmeu
      // matches **text**
      boldPattern: /(\*\*)(.*?)\1/gmi,
      // https://regexr.com/65i1l
      // matches # text
      h1Pattern: /^# ()(.+$)/gmi,
      h2Pattern: /^## ()(.+$)/gmi,
      h3Pattern: /^### ()(.+$)/gmi,
      h4Pattern: /^#### ()(.+$)/gmi,
      // https://regexr.com/65i1l
      // matches > text
      blockquotePattern: /^> ()(.+$)/gmi,
      // https://regexr.com/5jmf4
      // matches *text*
      emphasisPattern1: /(\*)(.*?)\1/gmi,
      // https://regexr.com/5jop0
      // matches _text_
      emphasisPattern2: /\b(_)(.*?)\1\b/gmi,
      // https://regexr.com/5jmf7
      // matches ~~text~~
      strikethroughPattern: /(~){2}(.*?)(~){2}/gmi,
      // https://regexr.com/5jr6k
      // matches ```⮐text⮐```
      codeBlockPattern: /(`){3}[\n ]*(.*?)[\n ]*(`){3}/gmis,
      // https://regexr.com/5jr6h
      // matches `text`
      codePattern: /(`)(.*?)\1/gmi
    }
  },
  markdownSegments (name) {
    this.typeCheck({ value: name, type: 'string', origin: 'markdownSegments' })
    const segments = []
    let currentPosition = 0
    if (!name) { return segments }
    while (currentPosition < name.length) {
      const markdown = this.markdown()
      const text = name.substring(currentPosition, name.length)
      const segment = { content: '', type: 'text' }
      let items = [
        {
          type: 'link',
          result: markdown.linkPattern.exec(text)
        }, {
          type: 'bold',
          result: markdown.boldPattern.exec(text)
        }, {
          type: 'h1',
          result: markdown.h1Pattern.exec(text)
        }, {
          type: 'h2',
          result: markdown.h2Pattern.exec(text)
        }, {
          type: 'h3',
          result: markdown.h3Pattern.exec(text)
        }, {
          type: 'h4',
          result: markdown.h4Pattern.exec(text)
        }, {
          type: 'blockquote',
          result: markdown.blockquotePattern.exec(text)
        }, {
          type: 'emphasis',
          result: markdown.emphasisPattern1.exec(text)
        }, {
          type: 'emphasis',
          result: markdown.emphasisPattern2.exec(text)
        }, {
          type: 'strikethrough',
          result: markdown.strikethroughPattern.exec(text)
        }, {
          type: 'codeBlock',
          result: markdown.codeBlockPattern.exec(text)
        }, {
          type: 'code',
          result: markdown.codePattern.exec(text)
        }
      ]
      items = items.map(item => {
        if (item.result) {
          item.startPosition = text.indexOf(item.result[0])
        }
        return item
      })
      items = sortBy(items, ['startPosition'])
      const match = items[0]
      if (match.startPosition === undefined) {
        segment.content = text
        currentPosition = name.length // ends loop
      } else {
        // before text
        const beforeSegment = {
          content: text.substring(0, match.startPosition),
          type: 'text'
        }
        segments.push(beforeSegment)
        // segment
        segment.content = match.result[2]
        segment.result = match.result
        segment.type = match.type
        currentPosition = currentPosition + match.startPosition + match.result[0].length
      }
      segments.push(segment)
    }
    return segments
  },
  itemNameMarkdownSegments (name) {
    // simplified markdown for box and list names
    const supportedTypes = ['text', 'link', 'bold', 'h1', 'h2', 'h3', 'h4', 'emphasis', 'strikethrough', 'code']
    const segments = this.markdownSegments(name)
    return segments.map(segment => {
      if (supportedTypes.includes(segment.type)) { return segment }
      return { type: 'text', content: segment.result[0] }
    })
  },
  commentPattern () {
    // https://regexr.com/5ju19
    // matches ((text))
    const commentPattern = /(\(\().*?(\)\))/gims
    return commentPattern
  },
  isNameComment (name) {
    if (!name) { return }
    const commentPattern = this.commentPattern()
    const comment = name.match(commentPattern)
    const markdown = this.markdown()
    const code = name.match(markdown.codeBlockPattern) || name.match(markdown.codePattern)
    let isCode
    if (!comment) { return }
    if (code) { isCode = code[0].includes(comment[0]) }
    if (isCode) { return }
    return Boolean(comment)
  },
  nameWithoutCommentPattern (name) {
    if (!name) { return }
    if (!this.isNameComment) { return name }
    name = name.replaceAll('((', '')
    name = name.replaceAll('))', '')
    return name
  },
  removeMarkdownCodeblocksFromString (string) {
    if (!string) { return '' }
    const segments = this.markdownSegments(string)
    segments.forEach(segment => {
      if (segment.type === 'codeBlock' || segment.type === 'code') {
        string = string.replace(segment.content, '')
      }
    })
    return string
  },
  languageFromCodeBlock (string) {
    // https://regexr.com/7lt5b
    // matches first word on first line
    const languagePattern = /^(\w)+\s/g
    let newString = string
    const match = string.match(languagePattern)
    if (!match) { return }
    const name = match[0].trim()
    // match language
    const language = codeLanguages.find(codeLanguage => {
      const isName = codeLanguage.name === name
      let isAlias
      if (codeLanguage.aliases) {
        isAlias = codeLanguage.aliases.includes(name)
      }
      return isName || isAlias
    })
    if (!language) { return }
    newString = string.replace(match[0], '')
    return { language, newString }
  },
  sanitizeString (string) {
    // Replace newlines, tabs, and other control characters with a space
    string = string.replace(/[\n\r\t]/g, ' ')
    // Replace double quotes with escaped quotes
    string = string.replace(/"/g, '\\"')
    // Replace backslashes with double backslashes
    string = string.replace(/\\/g, '\\\\')
    // Remove markdown syntax (e.g., **bold**, `code`, etc.)
    string = string.replace(/\*\*[^*]+\*\*/g, '') // Remove bold
    string = string.replace(/`[^`]+`/g, '') // Remove inline code
    string = string.replace(/\[\[.*?\]\]/g, '') // Remove double-bracketed links
    string = string.replace(/\[.*?\]\(.*?\)/g, '') // Remove markdown links
    // Optionally remove or encode other special characters
    string = string.replace(/[<>]/g, '') // Remove angle brackets (e.g., <, >)
    string = string.replace(/&/g, '&amp;') // Escape ampersand
    string = string.replace(/'/g, '&#39;') // Escape single quotes
    string = string.trim()
    return string
  },

  // Background Gradient

  backgroundGradientLayers () {
    const layers = []
    const numberOfLayers = 6
    times(numberOfLayers, function (index) {
      const layer = {
        x: random(140),
        y: random(140),
        color1: randomRGBA(1),
        color2: randomRGBA(0)
      }
      layers.push(layer)
    })
    const backgroundLayer = {
      color: randomRGBA(1)
    }
    layers.push(backgroundLayer)
    return layers
  },

  // import

  // https://jsoncanvas.org
  convertFromJsonCanvas (space) {
    const minPositionValue = 150
    let date = dayjs(new Date())
    date = date.format(consts.nameDateFormat)
    const newSpace = {}
    try {
      newSpace.name = `Canvas ${date}`
      newSpace.id = nanoid()
      newSpace.background = consts.defaultSpaceBackground
      newSpace.cards = []
      newSpace.connections = []
      // emsure node positions are positive 0,0
      const negativePositionOffset = {
        x: 0,
        y: 0
      }
      space.nodes.forEach(node => {
        if (node.x < negativePositionOffset.x) {
          negativePositionOffset.x = node.x
        }
        if (node.y < negativePositionOffset.y) {
          negativePositionOffset.y = node.y
        }
      })
      space.nodes = space.nodes.map(node => {
        node.x = node.x + Math.abs(negativePositionOffset.x)
        node.y = node.y + Math.abs(negativePositionOffset.y)
        return node
      })
      // nodes → cards
      const shouldNudgeCardsY = Boolean(space.nodes.find(node => node.y <= minPositionValue))
      const shouldNudgeCardsX = Boolean(space.nodes.find(node => node.x <= minPositionValue))
      space.nodes.forEach(node => {
        // url
        let shouldUpdateUrlPreview
        if (node.url) {
          shouldUpdateUrlPreview = true
        }
        // y
        let y = node.y
        if (shouldNudgeCardsY) {
          y += minPositionValue
        }
        // x
        let x = node.x
        if (shouldNudgeCardsX) {
          x += minPositionValue
        }
        // name
        let name = node.text || node.url || node.label
        if (node.file) {
          name = `\`${node.file}\``
        }
        const newCard = {
          id: node.id,
          x,
          y,
          backgroundColor: node.canvasColor || node.color,
          name,
          shouldUpdateUrlPreview
        }
        newSpace.cards.push(newCard)
      })
      // edges → connections
      const edgeColor = randomColor({ luminosity: 'light' })
      space.edges.forEach((edge, index) => {
        const newConnection = {
          id: edge.id,
          startItemId: edge.fromNode,
          endItemId: edge.toNode,
          controlPoint: consts.straightLineConnectionPathControlPoint,
          directionIsVisible: Boolean(edge.fromEnd === 'arrow' || edge.toEnd === 'arrow'),
          labelIsVisible: Boolean(edge.label),
          name: edge.label,
          color: edgeColor
        }
        newSpace.connections.push(newConnection)
      })
      return newSpace
    } catch (error) {
      console.error('🚒 convertFromCanvas', error)
    }
  }
}
